import * as T from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { WorldData, Vec3, Traversal, SimState } from './types';
import type { BranchBreak } from './simulation';
import { atmosphere, setSeeThrough, stylize } from './stylize';
import { branchStyle, branchTaper, spanPoint, taperedTube } from './branch-shape';
import { Scatter, foliageGeometry, foliageMat, type FoliageKind } from './foliage';
import { Sky } from './sky';
import { LightShafts } from './light-shafts';
import { SPECIES, barkColors, barkTube, isSpecies, plankButtresses, rootPillar, speciesForBiome, strangleStrands, trunkLiana, trunkShape, type CrownShape, type Species } from './trees';
const v = (p: Vec3) => new T.Vector3(...p);
/**
 * Material families (Design Bible §6/§10). `grip` is reserved for everything a hand can catch:
 * warmer, lighter bark with a soft warm rim. `deco` limbs are darker/cooler so they recede.
 * `foliage` is merged understory (ferns, flowers, hanging plants, palms); `far` is distant
 * scenery with a capped horizon haze instead of scene fog.
 */
const palette = { bark: '#875840', deco: '#5b3a2b', grip: '#b8743d', wood: '#ba8b4d', rope: '#ae9770', leaf: '#6b9d38', stone: '#c3bb98', roof: '#478355', waxy: '#7fbf45', foliage: '#ffffff', far: '#6f9a8c' };
type Kind = keyof typeof palette;
const MOSS: Partial<Record<Kind, T.Color>> = { bark: new T.Color('#7d9a3e'), deco: new T.Color('#5f7a35'), stone: new T.Color('#86a457') };
const grammar = {
    strong: { bark: '#a9643a', moss: '#93a245' },
    standard: { bark: '#b6703f', moss: '#9aa94a' },
    flexible: { bark: '#c28f57', moss: '#a3b955' },
    dead: { bark: '#cdbf9f', moss: '#cdbf9f', crack: '#7f6c55' },
    young: '#a8a35a',
    vine: '#4fa067',
    vineLight: '#86c77c',
};
/** Crown hues: emerald/yellow-green with a teal note (Bible §3), plus rare flowering accents. */
const CROWNS = ['#5aa443', '#74b440', '#4b9a4f', '#86bd3f', '#3f9258', '#68a83a'];
/** Flowering-tree crowns: soft, desaturated so they accent rather than shout. */
const FLOWERING = ['#e59ab9', '#e9a467', '#c4a3e0'];
const BLOSSOM = ['#ef8fb8', '#f4a24a', '#f6cf4e', '#c79ef0', '#ff8f73', '#fff4e0'];
function rng(seed: number) { return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
function leafGeometry() {
    const positions: number[] = [], indices: number[] = [];
    for (let i = 0; i <= 8; i++) {
        const t = i / 8, w = Math.sin(t * Math.PI) ** .75;
        for (let j = -1; j <= 1; j++)
            positions.push(j * w * .52, Math.sin(t * Math.PI) * .16 + Math.abs(j) * w * .11, t * 1.7);
    }
    for (let i = 0; i < 8; i++)
        for (let j = 0; j < 2; j++) {
            let a = i * 3 + j;
            indices.push(a, a + 3, a + 1, a + 1, a + 3, a + 4);
        }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
}
const leafGeo = leafGeometry();
const temp = new T.Object3D();
function grainTexture(kind: string) {
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 512;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = kind === 'bark' ? '#ede3d2' : '#f5ecda';
    ctx.fillRect(0, 0, 128, 512);
    for (let i = 0; i < 38; i++) {
        const x = (i * 37) % 128;
        ctx.strokeStyle = i % 3 === 0 ? '#70523824' : '#fffaf32b';
        ctx.lineWidth = i % 3 === 0 ? 2.5 : .8;
        ctx.beginPath();
        for (let y = 0; y <= 512; y += 8) {
            const px = x + Math.sin(y * .018 + i) * 3 + Math.sin(y * .041 + i) * 1.1;
            if (!y)
                ctx.moveTo(px, y);
            else
                ctx.lineTo(px, y);
        }
        ctx.stroke();
    }
    const texture = new T.CanvasTexture(c);
    texture.wrapS = texture.wrapT = T.RepeatWrapping;
    texture.colorSpace = T.SRGBColorSpace;
    texture.anisotropy = 4;
    return texture;
}
const barkTexture = grainTexture('bark'), woodTexture = grainTexture('wood');
/** Falling-water streaks; scrolled per frame (shared by every waterfall). */
const fallTexture = (() => {
    const c = document.createElement('canvas');
    c.width = 64; c.height = 256;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#bfeee8';
    ctx.fillRect(0, 0, 64, 256);
    for (let i = 0; i < 44; i++) {
        ctx.fillStyle = i % 3 ? 'rgba(255,255,255,.55)' : 'rgba(150,215,210,.5)';
        const x = (i * 23) % 64, y = (i * 71) % 256;
        ctx.fillRect(x, y, 2 + (i % 3), 30 + (i * 13) % 60);
        ctx.fillRect(x, y - 256, 2 + (i % 3), 30 + (i * 13) % 60);
    }
    const t = new T.CanvasTexture(c);
    t.wrapS = t.wrapT = T.RepeatWrapping;
    t.colorSpace = T.SRGBColorSpace;
    return t;
})();
const tmpColor = new T.Color();
/**
 * Transform + flatten a geometry into the batch layout (non-indexed, uv, vertex colour). A
 * geometry that already has colours is tinted by `color`; bark and stone get moss on top faces.
 */
function prepare(g: T.BufferGeometry, color: string, p: Vec3 = [0, 0, 0], scale: Vec3 = [1, 1, 1], rot: Vec3 = [0, 0, 0], kind?: Kind) {
    const geo = g.index ? g.toNonIndexed() : g.clone();
    for (const name of Object.keys(geo.attributes))
        if (!['position', 'normal', 'uv', 'color'].includes(name))
            geo.deleteAttribute(name);
    temp.position.set(...p);
    temp.rotation.set(...rot);
    temp.scale.set(...scale);
    temp.updateMatrix();
    geo.applyMatrix4(temp.matrix);
    if (!geo.getAttribute('normal'))
        geo.computeVertexNormals();
    if (!geo.getAttribute('uv'))
        geo.setAttribute('uv', new T.Float32BufferAttribute(new Float32Array(geo.getAttribute('position').count * 2), 2));
    const existing = geo.getAttribute('color');
    if (existing) {
        if (kind === 'foliage') {
            const tint = new T.Color(color);
            for (let i = 0; i < existing.count; i++)
                existing.setXYZ(i, existing.getX(i) * tint.r, existing.getY(i) * tint.g, existing.getZ(i) * tint.b);
        }
    }
    else {
        const c = new T.Color(color), colors = [];
        const pos = geo.getAttribute('position'), normal = geo.getAttribute('normal');
        const moss = kind ? MOSS[kind] : undefined;
        for (let i = 0; i < pos.count; i++) {
            const shade = .94 + .06 * Math.sin(pos.getY(i) * .6 + pos.getX(i) * .2);
            tmpColor.copy(c);
            if (moss) {
                // Warm moss settles on upward faces (Bible §7: rocks/limbs with moss).
                const up = smooth(normal.getY(i), .5, .92) * (.55 + .45 * Math.sin(pos.getX(i) * 1.3 + pos.getZ(i) * 1.1));
                tmpColor.lerp(moss, Math.max(0, up) * .75);
            }
            colors.push(tmpColor.r * shade, tmpColor.g * shade, tmpColor.b * shade);
        }
        geo.setAttribute('color', new T.Float32BufferAttribute(colors, 3));
    }
    return geo;
}
const sharedMaterials = new Map<Kind, T.MeshStandardMaterial>();
/** One material per family for the whole world (shared across regions and rebuilds). */
function material(kind: Kind) {
    let mat = sharedMaterials.get(kind);
    if (mat)
        return mat;
    if (kind === 'foliage')
        mat = foliageMat();
    else if (kind === 'far') {
        mat = new T.MeshStandardMaterial({ vertexColors: true, roughness: 1, fog: false });
        stylize(mat, { grade: true, haze: .7 });
    }
    else {
        const textured = kind === 'bark' || kind === 'deco' || kind === 'grip' ? barkTexture : kind === 'wood' ? woodTexture : null;
        mat = new T.MeshStandardMaterial({ vertexColors: true, map: textured, roughness: kind === 'waxy' ? .34 : kind === 'leaf' ? .87 : .96, metalness: kind === 'waxy' ? .04 : 0, side: kind === 'leaf' || kind === 'waxy' ? T.DoubleSide : T.FrontSide });
        stylize(mat, {
            fade: kind === 'grip' ? 'grip' : 'full',
            sway: kind === 'leaf',
            rim: kind === 'grip' ? { color: '#ffd394', strength: .3, power: 2.6 } : kind === 'waxy' ? { color: '#efffc2', strength: .3, power: 2 } : undefined,
        });
    }
    mat.userData.shared = true;
    sharedMaterials.set(kind, mat);
    return mat;
}
class Batch {
    groups = new Map<Kind, T.BufferGeometry[]>();
    /** Bark detail (lianas, root fringes, prop roots, twigs) dropped from the distant/Light LOD. */
    private detail = new Set<T.BufferGeometry>();
    pushDetail(geo: T.BufferGeometry) { this.detail.add(geo); this.push('bark', geo); }
    add(kind: Kind, g: T.BufferGeometry, p: Vec3 = [0, 0, 0], scale: Vec3 = [1, 1, 1], rot: Vec3 = [0, 0, 0], color?: string) {
        this.push(kind, prepare(g, color || palette[kind], p, scale, rot, kind));
    }
    push(kind: Kind, geo: T.BufferGeometry) {
        // Families that differ only in vertex colour share a draw call: limbs with trunks, rope
        // with wood, branch-tip leaves with the foliage material.
        if (kind === 'deco') kind = 'bark';
        else if (kind === 'rope') kind = 'wood';
        else if (kind === 'leaf') kind = 'foliage';
        if (!this.groups.has(kind))
            this.groups.set(kind, []);
        this.groups.get(kind)!.push(geo);
    }
    tube(kind: Kind, points: Vec3[], r: number, color?: string, taper = 1) {
        const curve = new T.CatmullRomCurve3(points.map(v));
        const rings = Math.max(6, points.length * 4), radial = r > .3 ? 8 : 6;
        const g = taperedTube(curve, rings, radial, u => r * (1 + (taper - 1) * u));
        g.computeVertexNormals();
        this.add(kind, g, [0, 0, 0], [1, 1, 1], [0, 0, 0], color);
        g.dispose();
    }
    /** A small plant from the shared foliage library, merged into this batch. */
    plant(kind: FoliageKind, p: T.Vector3Like, scale: number | Vec3, yaw: number, tint: string, tilt = 0) {
        const s: Vec3 = typeof scale === 'number' ? [scale, scale, scale] : scale;
        this.push('foliage', prepare(foliageGeometry()[kind].high, tint, [p.x, p.y, p.z], s, [tilt, yaw, 0], 'foliage'));
    }
    finish() {
        const group = new T.Group();
        for (const [kind, geos] of this.groups) {
            const g = mergeGeometries(geos);
            if (!g)
                continue;
            const mesh = new T.Mesh(g, material(kind));
            mesh.name = kind;
            if (kind === 'foliage') {
                mesh.userData.highGeometry = g;
                mesh.userData.lowGeometry = mergeGeometries(geos.filter((_, i) => i % 3 === 0)) ?? g;
            }
            else if (kind === 'bark' && geos.some(x => this.detail.has(x))) {
                // Bark LOD: the structure (trunks, limbs, buttresses) stays at every distance.
                const structure = geos.filter(x => !this.detail.has(x));
                mesh.userData.highGeometry = g;
                mesh.userData.lowGeometry = mesh.userData.farGeometry = (structure.length && mergeGeometries(structure)) || g;
            }
            mesh.castShadow = kind !== 'foliage' && kind !== 'far';
            mesh.receiveShadow = kind !== 'far';
            group.add(mesh);
            for (const geo of geos)
                geo.dispose();
        }
        return group;
    }
}
const ico = new T.IcosahedronGeometry(1, 1), sphere = new T.SphereGeometry(1, 10, 7), box = new T.BoxGeometry(1, 1, 1);
const smooth = (x: number, a: number, b: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/**
 * Data-driven keep-out volume for decoration: every catchable/standable object (with the space a
 * hanging, swinging monkey needs) and every authored route polyline. Decoration that would sit
 * inside it is moved or dropped, so it can never hide a route or read as a catch.
 */
class Clearance {
    private cells = new Map<number, number[]>();
    private segs: number[] = [];
    private static SIZE = 12;
    private static PAD = 8;
    constructor(data: WorldData, options: { objects?: boolean; route?: number; corridor?: (d: number) => number } = {}) {
        const routeR = options.route ?? 2.6, corridor = options.corridor ?? (d => 2.4 + d * .06);
        if (options.objects !== false) for (const o of data.objects) {
            const [ax, ay, az] = o.a, [bx, by, bz] = o.b;
            if (o.type === 0 && o.art === 'bridge') this.seg(ax, ay + 1.1, az, bx, by + 1.1, bz, 1.75);
            else if (o.type === 0) this.seg(ax, ay + .4, az, ax, ay + 2.6, az, o.radius + 1);
            else if (o.type === 1 || o.type === 3 || o.type === 7) this.seg(ax, ay - .6, az, bx, by - .6, bz, o.radius + 2.3);
            else if (o.type === 2) this.seg(ax, ay, az, bx, by, bz, 2);
            else if (o.type === 4) this.seg(ax, ay + .5, az, ax, ay + .5, az, o.radius + 1.6);
        }
        for (const route of data.routes || [])
            for (let i = 1; i < route.points.length; i++) {
                const [ax, ay, az] = route.points[i - 1], [bx, by, bz] = route.points[i];
                this.seg(ax, ay, az, bx, by, bz, routeR);
            }
        // Flight corridors: the open air between every pair of nearby catches (designed `catch`
        // points, else span midpoints) stays clear, so a destination always reads across its gap.
        const catches: Vec3[] = [];
        for (const o of data.objects) {
            const designed = (o as Traversal & { catch?: Vec3 }).catch;
            if ([1, 2, 3, 4, 7].includes(o.type)) catches.push(designed ?? (o.type === 2 ? [o.b[0], o.b[1] + 1, o.b[2]] : [(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2, (o.a[2] + o.b[2]) / 2]));
            else if (o.type === 0 && o.art !== 'bridge' && o.radius <= 4) catches.push([o.a[0], o.a[1] + 1, o.a[2]]);
        }
        for (let i = 0; i < catches.length; i++)
            for (let j = i + 1; j < catches.length; j++) {
                const [ax, ay, az] = catches[i], [bx, by, bz] = catches[j], d = Math.hypot(ax - bx, ay - by, az - bz);
                if (d > 1.5 && d < 17) this.seg(ax, ay - .8, az, bx, by - .8, bz, corridor(d));
            }
    }
    /** Horizontal distance check against gameplay space, for whole decorative trees. */
    open(x: number, z: number, y0: number, y1: number, radius: number) {
        for (let y = y0; y <= y1; y += 2)
            if (!this.free(x, y, z, radius)) return false;
        return true;
    }
    private key(x: number, z: number) { return (Math.floor(x / Clearance.SIZE) + 4096) * 8192 + Math.floor(z / Clearance.SIZE) + 4096; }
    private seg(ax: number, ay: number, az: number, bx: number, by: number, bz: number, r: number) {
        const index = this.segs.length / 7;
        this.segs.push(ax, ay, az, bx, by, bz, r);
        const pad = r + Clearance.PAD, S = Clearance.SIZE;
        for (let x = Math.floor((Math.min(ax, bx) - pad) / S); x <= Math.floor((Math.max(ax, bx) + pad) / S); x++)
            for (let z = Math.floor((Math.min(az, bz) - pad) / S); z <= Math.floor((Math.max(az, bz) + pad) / S); z++) {
                const k = (x + 4096) * 8192 + z + 4096;
                let cell = this.cells.get(k);
                if (!cell) this.cells.set(k, cell = []);
                cell.push(index);
            }
    }
    /** True when a sphere of `radius` (≤ 6 m) at p keeps clear of all gameplay space. */
    free(x: number, y: number, z: number, radius: number) {
        const cell = this.cells.get(this.key(x, z));
        if (!cell) return true;
        const s = this.segs;
        for (const i of cell) {
            const o = i * 7, ax = s[o], ay = s[o + 1], az = s[o + 2], dx = s[o + 3] - ax, dy = s[o + 4] - ay, dz = s[o + 5] - az;
            const len = dx * dx + dy * dy + dz * dz;
            const t = len > 1e-6 ? Math.min(1, Math.max(0, ((x - ax) * dx + (y - ay) * dy + (z - az) * dz) / len)) : 0;
            const ex = ax + dx * t - x, ey = ay + dy * t - y, ez = az + dz * t - z, r = s[o + 6] + radius;
            if (ex * ex + ey * ey + ez * ez < r * r) return false;
        }
        return true;
    }
    /** A vertical column (trunk) from y0 to y1 stays clear. */
    column(x: number, z: number, y0: number, y1: number, radius: number) {
        for (let y = y0; y <= y1; y += 2.5)
            if (!this.free(x, y, z, radius)) return false;
        return true;
    }
}

/** Per-ring vertex colours for a TubeGeometry laid out as (segments+1)×(radial+1). */
function paintTube(g: T.BufferGeometry, segments: number, radial: number, paint: (u: number, j: number, ny: number, out: T.Color) => void) {
    const normal = g.getAttribute('normal'), colors = new Float32Array(normal.count * 3), c = new T.Color();
    for (let i = 0; i <= segments; i++)
        for (let j = 0; j <= radial; j++) {
            const ix = i * (radial + 1) + j;
            paint(i / segments, j, normal.getY(ix), c);
            colors[ix * 3] = c.r; colors[ix * 3 + 1] = c.g; colors[ix * 3 + 2] = c.b;
        }
    g.setAttribute('color', new T.BufferAttribute(colors, 3));
}

/** A trunk the renderer can hang limbs from: a hero trunk from the map or a synthetic support. */
type Trunk = { id?: number; x: number; z: number; r: number; bottom: number; top: number; synthetic: boolean; taken: number[]; high: number; species: Species; variant: number; bark: T.Color; dark: T.Color };
/** Species/variant for a map trunk (authored by the generator), else from its region's biome mix. */
function trunkIdentity(o: { species?: unknown; variant?: unknown; id?: number; a?: Vec3 }, biome: string, seed: number) {
    const variant = typeof o.variant === 'number' ? o.variant : Math.abs(Math.round(seed * 7919 + (o.id ?? 0) * 104729 + (o.a ? o.a[0] * 131 + o.a[2] * 71 : 0))) % 1000003;
    const species: Species = isSpecies(o.species) ? o.species : speciesForBiome(biome, (Math.sin(variant * 3.17) * .5 + .5) % 1);
    return { species, variant, ...barkColors(SPECIES[species], variant) };
}

/** Crown recipes per silhouette (Bible §7: memorable silhouettes; grouped leaf clusters). */
const CROWN: Record<CrownShape, { count: number; ring: [number, number]; lift: number; flatten: number; scale: [number, number]; droop: number }> = {
    umbrella: { count: 10, ring: [.9, 2.05], lift: .14, flatten: .55, scale: [.72, .95], droop: .1 },
    dome: { count: 7, ring: [.55, 1.05], lift: .42, flatten: .88, scale: [.72, .95], droop: .15 },
    dense: { count: 9, ring: [.4, .95], lift: .38, flatten: .85, scale: [.66, .86], droop: .2 },
    spread: { count: 10, ring: [.85, 1.95], lift: .2, flatten: .68, scale: [.7, .92], droop: .3 },
    round: { count: 6, ring: [.5, .95], lift: .38, flatten: .9, scale: [.72, .95], droop: .15 },
};
/**
 * Places a crown of sculpted clumps around `c` in the species' silhouette. Each clump keeps clear of
 * gameplay space; a blocked clump first tries to rise above the route, otherwise it is dropped.
 * `blossom` tints most clumps with the tree's flowering hue (the rest stay green).
 */
function crown(scatter: Scatter, plants: Batch, clear: Clearance, c: T.Vector3, size: number, shape: CrownShape, hue: string, random: () => number, accents = 0, blossom = '', countScale = 1) {
    const placed: { p: T.Vector3; s: number }[] = [];
    const recipe = CROWN[shape], base = new T.Color(hue), bloom = blossom ? new T.Color(blossom) : null;
    const count = Math.max(2, Math.round(recipe.count * countScale));
    for (let i = 0; i < count; i++) {
        const a = i * 2.39996 + random() * .5, ring = i === 0 ? 0 : T.MathUtils.lerp(recipe.ring[0], recipe.ring[1], (i % 3) / 2 * .6 + random() * .4);
        const d = size * ring, s = size * (i === 0 ? recipe.scale[1] * 1.1 : T.MathUtils.lerp(recipe.scale[0], recipe.scale[1], random()));
        const p = new T.Vector3(c.x + Math.cos(a) * d, c.y + (i === 0 ? size * recipe.lift : (random() - .35) * size * recipe.lift) - Math.max(0, ring - recipe.ring[0]) * size * recipe.droop, c.z + Math.sin(a) * d);
        let ok = false;
        for (const lift of [0, 1.5, 3, 4.5])
            if (clear.free(p.x, p.y + lift, p.z, s * .82)) { p.y += lift; ok = true; break; }
        if (!ok) continue;
        const flowering = bloom && random() < .62;
        const color = (flowering ? bloom! : base).clone().offsetHSL((random() - .5) * .04, (random() - .5) * .1, (random() - .5) * .08);
        scatter.add(random() < .5 ? 'clump' : 'clumpB', p, new T.Vector3(s, s * recipe.flatten * (.92 + random() * .2), s), random() * Math.PI * 2, color, (random() - .5) * .25);
        placed.push({ p, s });
        // Detail tier C: blossoms on top and short leafy curtains below the outer clumps.
        if ((flowering || (accents > 0 && random() < accents))) {
            const tint = flowering ? blossom : BLOSSOM[Math.floor(random() * BLOSSOM.length)];
            for (let k = 0; k < (flowering ? 6 : 4); k++) {
                const fa = random() * Math.PI * 2, fr = s * (.2 + random() * .55);
                plants.plant('flower', { x: p.x + Math.cos(fa) * fr, y: p.y + s * recipe.flatten * .62 * (1 - (fr / s) ** 2 * .6), z: p.z + Math.sin(fa) * fr }, .26 + random() * .14, random() * 6, tint, (random() - .5) * .4);
            }
        }
        if (i > 0 && random() < .5) {
            const len = 1.4 + random() * 1.8, hx = p.x + Math.cos(a) * s * .55, hz = p.z + Math.sin(a) * s * .55, hy = p.y - s * recipe.flatten * .45;
            if (clear.free(hx, hy - len * .5, hz, .6) && clear.free(hx, hy - len, hz, .5))
                plants.plant(random() < .5 ? 'hang' : 'beard', { x: hx, y: hy, z: hz }, [1.6, len, 1.6], random() * 6, random() < .5 ? '#5f8f43' : '#7d8f4a');
        }
    }
    return placed;
}

/** Picks `n` azimuths spread as far as possible from the taken ones (gameplay limbs). */
function freeAzimuths(taken: number[], n: number, random: () => number) {
    const picked: number[] = [], diff = (a: number, b: number) => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
    for (let k = 0; k < n; k++) {
        let best = random() * Math.PI * 2, score = -1;
        for (let i = 0; i < 24; i++) {
            const a = i / 24 * Math.PI * 2 + random() * .2;
            const others = [...taken, ...picked];
            const s = others.length ? Math.min(...others.map(o => diff(a, o))) : 1;
            if (s > score) { score = s; best = a; }
        }
        picked.push(best);
    }
    return picked;
}
/** True when every sample along a→b keeps `radius` clear of gameplay space. */
function lineFree(clear: Clearance, a: T.Vector3, b: T.Vector3, radius: number, samples = 4) {
    for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        if (!clear.free(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t, radius)) return false;
    }
    return true;
}

type TreeOptions = { crownTop: number; size: number; hero: boolean; emergent?: boolean; accents?: number; crownCount?: number; decor?: boolean };
/** Leaders the trunk forks into above its climbable span, and how steeply they rise. */
const LEADERS: Record<Species, { n: number; elevation: [number, number] }> = {
    kapok: { n: 3, elevation: [.55, .75] }, banyan: { n: 3, elevation: [.6, .85] }, flowering: { n: 3, elevation: [.75, 1.0] },
    mahogany: { n: 2, elevation: [.95, 1.15] }, strangler: { n: 2, elevation: [.9, 1.1] }, mangrove: { n: 2, elevation: [.8, 1.0] },
};
/**
 * A complete species tree (Bible §7): trunk with its species base (kapok planks, buttress roots or
 * mangrove stilts), strangler lattice, hugging lianas, structural limbs at every height in the gaps
 * between gameplay limbs (thick collar at the trunk, forking branchlets, leaf clusters, epiphytes,
 * banyan prop roots and strangler root fringes), a species crown, a leafy skirt framing the swing
 * height beside — never inside — the flight corridors, and an understory at the roots.
 */
function growTree(batch: Batch, scatter: Scatter, plants: Batch, clear: Clearance, t: Trunk, random: () => number, o: TreeOptions) {
    // Decorative trees are not colliders: they may lean and fork from mid-height.
    const p = SPECIES[t.species], bottom = t.bottom - 2, climbTop = o.decor ? T.MathUtils.lerp(bottom, o.crownTop, .5 + random() * .12) : Math.min(t.top, o.crownTop - 3);
    const shape = trunkShape(t.x, t.z, bottom, climbTop, o.crownTop, t.r, p, random, t.bark, t.dark);
    batch.push('bark', prepare(shape.geometry, p.bark));
    shape.geometry.dispose();
    const { radiusAt, centerAt } = shape, top = shape.curve.getPointAt(1);
    const pushBark = (g: T.BufferGeometry, detail = false) => { const geo = prepare(g, p.bark); if (detail) batch.pushDetail(geo); else batch.push('bark', geo); g.dispose(); };
    const water = Math.min(-1.4, bottom + 1);
    // Species base.
    if (p.base === 'planks') plankButtresses(t.x, t.z, t.r, water, t.bark, t.dark, random).forEach(g => pushBark(g));
    else if (p.base === 'stilts') {
        const n = 7 + Math.floor(random() * 4);
        for (let i = 0; i < n; i++) {
            const a = i / n * Math.PI * 2 + random() * .4, y = Math.max(1.5, bottom + 3) + random() * 3.5, c = centerAt(y);
            const from = new T.Vector3(c.x + Math.cos(a) * radiusAt(y) * .8, y, c.z + Math.sin(a) * radiusAt(y) * .8), reach = t.r * (2.4 + random() * 1.6);
            pushBark(rootPillar(from, new T.Vector3(t.x + Math.cos(a) * reach, -1.6, t.z + Math.sin(a) * reach), .16 + t.r * .07, t.bark, t.dark, 1.1 + random() * .8, random));
        }
    }
    else {
        const n = 5 + Math.floor(random() * 3);
        for (let i = 0; i < n; i++) {
            const a = i / n * Math.PI * 2 + random() * .45, dx = Math.cos(a), dz = Math.sin(a), reach = t.r * (2.6 + random() * 1.4);
            pushBark(barkTube([new T.Vector3(t.x + dx * reach, water - .2, t.z + dz * reach), new T.Vector3(t.x + dx * reach * .55, .7, t.z + dz * reach * .55), new T.Vector3(t.x + dx * t.r * .55, 3.2 + random() * 2.2, t.z + dz * t.r * .55)], t.r * .2, t.r * .5, t.bark, t.dark, 5), true);
        }
    }
    if (p.lattice) strangleStrands(centerAt, radiusAt, water, climbTop, t.bark.clone().lerp(new T.Color('#c8c2a0'), .25), t.dark, random).forEach(g => pushBark(g));
    if (random() < p.lianas) pushBark(trunkLiana(centerAt, radiusAt, water, Math.max(6, climbTop - 2 - random() * 6), random), true);
    if (random() < p.lianas * .5) pushBark(trunkLiana(centerAt, radiusAt, water, Math.max(6, climbTop * .6), random), true);
    const hue = p.hues[Math.floor(random() * (p.blossom ? 1 : p.hues.length))] , green = SPECIES.mahogany.hues[Math.floor(random() * 3)];
    const blossom = p.blossom ? p.hues[Math.floor(random() * p.hues.length)] : '';
    const leaf = p.blossom ? green : hue;
    // Structural limbs at every height, in the azimuth gaps between gameplay limbs.
    const nLimbs = p.limbs[0] + Math.floor(random() * (p.limbs[1] - p.limbs[0] + 1)) + (o.hero ? 2 : -1);
    // Hero limbs fill the climbable span (swing height); above it the leaders take over.
    const lowY = Math.max(bottom + 7, 5), highY = o.hero ? Math.max(lowY + 4, climbTop + .5) : o.crownTop - 2.5;
    for (const [k, a] of freeAzimuths(t.taken, nLimbs, random).entries()) {
        const y0 = T.MathUtils.lerp(lowY, highY, Math.min(1, (k + .35 + random() * .5) / nLimbs) ** (o.emergent ? .6 : .85));
        const dx = Math.cos(a), dz = Math.sin(a), c0 = centerAt(y0), r0 = radiusAt(y0);
        const limbR = Math.min(r0 * .4, .22 + o.size * .07) * (y0 > climbTop ? .8 : 1);
        for (const scale of [1, .7, .45]) {
            const len = o.size * (1.3 + random() * .8) * p.limbLength * scale, rise = p.limbRise * (.7 + random() * .6);
            const tip = new T.Vector3(c0.x + dx * (r0 + len), y0 + len * rise, c0.z + dz * (r0 + len));
            const mid = new T.Vector3(c0.x + dx * (r0 + len * .5), y0 + len * rise * .35 + .25, c0.z + dz * (r0 + len * .5));
            const surface = new T.Vector3(c0.x + dx * r0, y0, c0.z + dz * r0);
            if (!lineFree(clear, surface, mid, limbR + .5, 2) || !lineFree(clear, mid, tip, limbR + .9, 2)) continue;
            const root = new T.Vector3(c0.x + dx * r0 * .45, y0 - limbR * .6, c0.z + dz * r0 * .45);
            // Thick at the trunk with a flared collar, tapering out; a gentle S toward the tip.
            pushBark(barkTube([root, surface, mid, tip], limbR * 1.9, limbR * .28, t.bark, t.dark, limbR > .3 ? 7 : 5, u => limbR * (u < .12 ? T.MathUtils.lerp(2.1, 1.25, u / .12) : T.MathUtils.lerp(1.25, .3, (u - .12) / .88))));
            // Forking branchlets with leaf clusters near the tip.
            const forks = o.hero ? 1 + Math.floor(random() * 2) : 1;
            const clusterAt = (q: T.Vector3, s: number) => {
                if (clear.free(q.x, q.y + s * .2, q.z, s * .8)) scatter.add(random() < .5 ? 'clump' : 'clumpB', { x: q.x, y: q.y + s * .2, z: q.z }, new T.Vector3(s, s * CROWN[p.crown].flatten, s), random() * 6, new T.Color(blossom && random() < .5 ? blossom : leaf).offsetHSL(0, (random() - .5) * .1, (random() - .5) * .08), (random() - .5) * .3);
            };
            for (let f = 0; f < forks; f++) {
                const u = .5 + random() * .25, from = mid.clone().lerp(tip, (u - .5) * 2), side = (f % 2 ? 1 : -1) * (.6 + random() * .5);
                const fa = a + side, fl = len * (.35 + random() * .2);
                const end = new T.Vector3(from.x + Math.cos(fa) * fl, from.y + fl * (.35 + random() * .3), from.z + Math.sin(fa) * fl);
                if (!lineFree(clear, from, end, .7, 2)) continue;
                pushBark(barkTube([from, from.clone().lerp(end, .5).add(new T.Vector3(0, .15, 0)), end], limbR * .5, limbR * .18, t.bark, t.dark, 6), true);
                clusterAt(end, o.size * (.42 + random() * .2) * p.size);
            }
            clusterAt(tip, o.size * (.55 + random() * .25) * p.size);
            // Epiphytes ride the limb: bromeliads (crimson heart), ferns, moss beards beneath.
            for (let e = 0; e < (o.hero ? 2 : 1); e++) {
                if (random() > .75) continue;
                const q = surface.clone().lerp(tip, .25 + random() * .45);
                if (!clear.free(q.x, q.y + .4, q.z, .6)) continue;
                const top = q.y + limbR * .85;
                if (random() < .55) {
                    plants.plant('bromeliad', { x: q.x, y: top, z: q.z }, .55 + random() * .35, random() * 6, random() < .5 ? '#86b84f' : '#9cc45a');
                    plants.plant('flower', { x: q.x, y: top + .12, z: q.z }, .14, random() * 6, random() < .5 ? '#ff6f5e' : '#f7a640');
                }
                else plants.plant('fern', { x: q.x, y: top - .05, z: q.z }, .7 + random() * .4, random() * 6, '#8fc05a');
            }
            if (random() < .6) {
                const q = mid.clone().lerp(tip, random() * .6), len2 = 1 + random() * 1.4;
                if (clear.free(q.x, q.y - len2 * .5, q.z, .5)) plants.plant('beard', { x: q.x, y: q.y - limbR, z: q.z }, [1.3, len2, 1.3], random() * 6, '#8d9a58');
            }
            // A liana loop sags from the limb back to the trunk: a curve, never a clean hanging line.
            if (o.hero && random() < .45) {
                const q = surface.clone().lerp(tip, .55 + random() * .3), back = centerAt(y0 - 1.5 - random() * 2), out = new T.Vector3(dx, 0, dz);
                const anchor = back.clone().addScaledVector(out, radiusAt(back.y) * 1.02), sagY = Math.min(q.y, anchor.y) - 1.6 - random() * 1.8;
                const low = q.clone().lerp(anchor, .5).setY(sagY);
                if (lineFree(clear, q, low, .5, 2) && lineFree(clear, low, anchor, .5, 2))
                    pushBark(barkTube([q, q.clone().lerp(low, .6).setY((q.y + sagY) / 2 - .4), low, low.clone().lerp(anchor, .5).setY((sagY + anchor.y) / 2 - .4), anchor], .05, .065, new T.Color('#6f6b3c'), new T.Color('#4f4c2a'), 4), true);
            }
            // Banyan prop roots drop from the limb to the water; strangler aerial roots fringe it.
            if (p.props && y0 < 24) for (let r = 0; r < 1 + Math.floor(random() * 2); r++) {
                const q = surface.clone().lerp(tip, .35 + random() * .5), foot = new T.Vector3(q.x + (random() - .5), -1.6, q.z + (random() - .5));
                if (q.y < 3 || !lineFree(clear, q, foot, .7, Math.ceil(q.y / 3))) continue;
                pushBark(rootPillar(q, foot, .17 + random() * .1, t.bark.clone().lerp(t.dark, .2), t.dark, 0, random), true);
            }
            if (p.fringe) for (let r = 0; r < 2; r++) {
                const q = surface.clone().lerp(tip, .3 + random() * .6), drop = 2 + random() * 3.5, foot = new T.Vector3(q.x + (random() - .5) * .4, q.y - drop, q.z + (random() - .5) * .4);
                if (!lineFree(clear, q, foot, .45, 2)) continue;
                pushBark(barkTube([q, q.clone().lerp(foot, .5).add(new T.Vector3((random() - .5) * .3, 0, (random() - .5) * .3)), foot], .055, .025, t.dark.clone().lerp(t.bark, .4), t.dark, 4), true);
            }
            break;
        }
    }
    // Above the climbable span the trunk forks into leaning leaders; the crown spreads over their
    // tips and the trunk top in the species' silhouette.
    const crownSize = o.size * p.size * (o.emergent ? 1.2 : 1), lead = LEADERS[t.species], tips: T.Vector3[] = [];
    const forkY = climbTop + .3, rise = o.crownTop - forkY;
    if (rise > 2.5) {
        const turn = random() * Math.PI * 2, start = centerAt(forkY - .4), r0 = radiusAt(forkY) * .62;
        for (let i = 0; i < lead.n; i++) {
            const az = turn + i / lead.n * Math.PI * 2 + (random() - .5) * .6;
            for (const steep of [0, .35, .7]) {
                const el = Math.min(1.35, T.MathUtils.lerp(lead.elevation[0], lead.elevation[1], random()) + steep), len = Math.min(crownSize * 1.9, rise * 1.25 / Math.sin(el) + 1);
                const dir = new T.Vector3(Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el));
                const tip = start.clone().addScaledVector(dir, len), mid = start.clone().addScaledVector(dir, len * .5).add(new T.Vector3(0, len * .08, 0));
                if (!lineFree(clear, mid, tip, r0 * .5 + .5, 2)) continue;
                pushBark(barkTube([start.clone().addScaledVector(dir, -r0 * .5), mid, tip], r0, r0 * .3, t.bark, t.dark, 7));
                tips.push(tip);
                break;
            }
        }
    }
    const share = tips.length ? .55 : 1, crowns = (o.crownCount ?? 1) * (o.emergent ? 1.1 : 1) * (o.decor && !o.emergent ? .8 : 1);
    crown(scatter, plants, clear, top, crownSize, p.crown, leaf, random, o.accents ?? .15, blossom, crowns * share);
    for (const q of tips) crown(scatter, plants, clear, q, crownSize * .72, p.crown, leaf, random, o.accents ?? .15, blossom, crowns * .38);
    // A leafy skirt around the trunk at swing height, kept beside (never in) the flight corridors.
    if (o.hero) {
        let placed = 0;
        const skirtTop = Math.min(climbTop, Math.max(t.high + 2, lowY + 4)), skirtLow = Math.max(lowY, skirtTop - 11);
        for (let i = 0; i < 18 && placed < 5; i++) {
            const a = random() * Math.PI * 2, y = T.MathUtils.lerp(skirtLow, skirtTop, random()), c = centerAt(y), s = 1.4 + random() * 1.3;
            const d = radiusAt(y) + s * (.55 + random() * .5), q = new T.Vector3(c.x + Math.cos(a) * d, y, c.z + Math.sin(a) * d);
            if (!clear.free(q.x, q.y, q.z, s * .85)) continue;
            scatter.add(random() < .5 ? 'clump' : 'clumpB', q, new T.Vector3(s, s * .8, s), random() * 6, new T.Color(leaf).offsetHSL(0, -.04, -.05 + random() * .06), .2);
            if (random() < .5) plants.plant('fern', { x: q.x, y: q.y - s * .4, z: q.z }, .9 + random() * .4, a, '#8fc05a', .3);
            placed++;
        }
    }
    // Epiphyte tufts clinging to the trunk itself (Bible §7 secondary foliage).
    for (let k = 0; k < 2; k++) {
        const a = random() * Math.PI * 2, y = T.MathUtils.lerp(Math.max(3, bottom + 6), o.crownTop - 5, (k + .3 + random() * .4) / 2);
        const c = centerAt(y), d = radiusAt(y) + .1, q = new T.Vector3(c.x + Math.cos(a) * d, y, c.z + Math.sin(a) * d);
        if (!clear.free(q.x, q.y, q.z, 1.2)) continue;
        plants.plant(random() < .6 ? 'bromeliad' : 'fern', q, .8 + random() * .4, a, '#8fc05a', .5);
        if (random() < .5 && clear.free(q.x, q.y - 2, q.z, .6)) plants.plant('beard', { x: q.x, y: q.y - .2, z: q.z }, [1.4, 1.4 + random(), 1.4], random() * 6, '#7f8f4c');
    }
    // Understory at the roots: a mossy root mound, ferns, giant leaves, lily pads and a palm or tree fern.
    if (bottom < 1) understory(batch, plants, clear, t.x, t.z, t.r, random);
}

/** Lush ground layer around a tree standing in the river (Bible §8: looking down reads alive). */
function understory(batch: Batch, plants: Batch, clear: Clearance, x: number, z: number, r: number, random: () => number) {
    // The mound shares the bark draw (vertex colour only; moss settles on its top).
    batch.add('bark', ico, [x, -1.2, z], [r * 3.1, 1.9, r * 3.1], [0, random() * 6, 0], '#8c8a62');
    for (let i = 0; i < 7; i++) {
        const a = random() * Math.PI * 2, d = r * (1.4 + random() * 1.5), px = x + Math.cos(a) * d, pz = z + Math.sin(a) * d;
        const py = .55 * Math.max(0, 1 - (d / (r * 3.1)) ** 2) - .05;
        if (!clear.free(px, py + 1, pz, 1)) continue;
        if (i % 2 === 0) plants.plant('giant', { x: px, y: py, z: pz }, 1.3 + random() * .9, random() * 6, random() < .5 ? '#6faa45' : '#82b84c');
        else plants.plant('fern', { x: px, y: py, z: pz }, 1.2 + random() * .9, random() * 6, random() < .5 ? '#9ccc5c' : '#7fb54e');
    }
    for (let i = 0; i < 4; i++) {
        const a = random() * Math.PI * 2, d = r * 3.2 + 1 + random() * 5;
        plants.plant('lily', { x: x + Math.cos(a) * d, y: .02, z: z + Math.sin(a) * d }, 1 + random() * .8, random() * 6, random() < .5 ? '#6fae4c' : '#8cbc58');
    }
    if (random() < .75) {
        const a = random() * Math.PI * 2, d = r * (2.2 + random()), px = x + Math.cos(a) * d, pz = z + Math.sin(a) * d, h = 3.5 + random() * 3;
        if (clear.column(px, pz, .5, h + 2, 1.2)) {
            if (random() < .5) {
                batch.tube('bark', [[px, -.4, pz], [px + Math.cos(a) * .4, h * .5, pz + Math.sin(a) * .4], [px + Math.cos(a) * .9, h, pz + Math.sin(a) * .9]], .17, '#6b5340', .7);
                plants.plant('fern', { x: px + Math.cos(a) * .9, y: h - .1, z: pz + Math.sin(a) * .9 }, [2.6, 2, 2.6], random() * 6, '#8cc257');
            }
            else {
                batch.tube('bark', [[px, -.4, pz], [px + Math.cos(a) * h * .2, h * .5, pz + Math.sin(a) * h * .2], [px + Math.cos(a) * h * .45, h, pz + Math.sin(a) * h * .45]], .18, '#9a7650', .55);
                plants.plant('palm', { x: px + Math.cos(a) * h * .45, y: h, z: pz + Math.sin(a) * h * .45 }, [2.6, 2.3, 2.6], random() * 6, random() < .5 ? '#7cb84a' : '#8cc152');
            }
        }
    }
}

/** Mountain massif: radial grid with ridges, forest skirt and pale rock shoulders. */
function mountainGeometry(seed: number, sharp: number) {
    const random = rng(seed), radial = 28, rings = 9, positions: number[] = [], colors: number[] = [], indices: number[] = [];
    const forest = new T.Color('#3d6e57'), rock = new T.Color('#6d8492'), cream = new T.Color('#c9cdc4');
    const phase = random() * 6, ridges = 3 + Math.floor(random() * 3);
    for (let i = 0; i <= rings; i++) {
        const h = i / rings;
        for (let j = 0; j <= radial; j++) {
            const a = j / radial * Math.PI * 2;
            const wobble = 1 + .2 * Math.sin(a * ridges + phase) + .09 * Math.sin(a * 7 + phase * 2);
            const r = (1 - h) ** sharp * wobble * (i === rings ? 0 : 1);
            const y = h * (1 + .06 * Math.sin(a * ridges + phase) * (1 - h));
            positions.push(Math.cos(a) * r, y, Math.sin(a) * r);
            const c = forest.clone().lerp(rock, smooth(h, .35, .7)).lerp(cream, smooth(h, .82, 1) * .45);
            c.multiplyScalar(.9 + .1 * Math.sin(a * 5 + h * 9));
            colors.push(c.r, c.g, c.b);
        }
    }
    for (let i = 0; i < rings; i++)
        for (let j = 0; j < radial; j++) {
            const a = i * (radial + 1) + j, b = a + radial + 1;
            indices.push(a, b, a + 1, a + 1, b, b + 1);
        }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    g.setAttribute('color', new T.Float32BufferAttribute(colors, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
}
function deck(batch: Batch, o: Traversal) {
    if (o.art === 'bridge') {
        const a = v(o.a), b = v(o.b), d = b.clone().sub(a), len = d.length(), yaw = Math.atan2(d.x, d.z);
        const steps = Math.ceil(len / .48);
        for (let i = 0; i <= steps; i++) {
            const p = a.clone().lerp(b, i / steps);
            batch.add('wood', box, [p.x, p.y - .13, p.z], [2.1, .20, .40], [0, yaw, 0], i % 3 === 0 ? '#bd945a' : '#b18a52');
        }
        const normal = new T.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
        for (const side of [-1, 1]) {
            const p = a.clone().addScaledVector(normal, side * .96), q = b.clone().addScaledVector(normal, side * .96);
            batch.tube('rope', [[p.x, p.y + 1, p.z], [q.x, q.y + 1, q.z]], .045);
            batch.tube('wood', [[p.x, p.y - .2, p.z], [p.x, p.y + 1.1, p.z]], .055);
        }
    }
    else {
        const g = new T.CylinderGeometry(o.radius, o.radius * .92, .5, 20);
        batch.add(o.art === 'stone' ? 'stone' : 'wood', g, [o.a[0], o.a[1] - .25, o.a[2]]);
        g.dispose();
        if (o.art !== 'stone')
            for (let i = -4; i <= 4; i++) {
                const x = i * o.radius / 5, l = Math.sqrt(o.radius ** 2 - x ** 2) * 2;
                batch.add('bark', box, [o.a[0] + x, o.a[1] + .007, o.a[2]], [.028, .016, l]);
            }
    }
}
function hut(batch: Batch, p: Vec3, scale = 1) {
    const [x, y, z] = p;
    const r = 2.3 * scale;
    batch.add('wood', new T.CylinderGeometry(r, r, .2, 12), [x, y, z]);
    for (let i = 0; i < 8; i++) {
        const a = i * Math.PI / 4;
        batch.tube('wood', [[x + Math.cos(a) * r, y, z + Math.sin(a) * r], [x + Math.cos(a) * r, y + 2.5 * scale, z + Math.sin(a) * r]], .09 * scale);
        if (i !== 0 && i !== 7)
            batch.add('wood', box, [x + Math.cos(a) * r, y + .65 * scale, z + Math.sin(a) * r], [1.8 * scale, 1.1 * scale, .09], [0, -a + Math.PI / 2, 0]);
    }
    for (let j = 0; j < 4; j++) {
        const g = new T.ConeGeometry((3 - j * .55) * scale, (1.6 - j * .1) * scale, 8);
        batch.add('roof', g, [x, y + (2.7 + j * .33) * scale, z], [1, 1, 1], [0, Math.PI / 8, 0], j % 2 ? '#647e39' : '#728f40');
        g.dispose();
    }
}
function temple(batch: Batch, p: Vec3) {
    const [x, y, z] = p;
    for (let i = 0; i < 4; i++)
        batch.add('stone', new T.CylinderGeometry(6 - i * .8, 6.4 - i * .8, 1, 8), [x, y + i * .8, z], [1, 1, 1], [0, Math.PI / 8, 0]);
    for (const sx of [-1, 1])
        for (const sz of [-1, 1]) {
            const px = x + sx * 2.4, pz = z + sz * 2.4;
            batch.add('stone', new T.CylinderGeometry(.5, .72, 6, 8), [px, y + 5, pz]);
            for (let j = 0; j < 4; j++)
                batch.add('stone', box, [px, y + 2 + j * 1.4, pz], [1.25, .3, 1.25]);
        }
    batch.add('stone', box, [x, y + 8, z], [6.7, .8, 6.7]);
    for (let j = 0; j < 4; j++)
        batch.add('stone', new T.CylinderGeometry(3 - j * .68, 3.8 - j * .68, 1, 4), [x, y + 8.7 + j * .8, z], [1, 1, 1], [0, Math.PI / 4, 0]);
}
/** Leaf card with both faces, for meshes that use a single-sided material. */
const leafGeo2 = (() => {
    const back = leafGeo.clone(), index = back.getIndex()!.array as ArrayLike<number>, flipped: number[] = [];
    for (let i = 0; i < index.length; i += 3)
        flipped.push(index[i], index[i + 2], index[i + 1]);
    back.setIndex(flipped);
    back.computeVertexNormals();
    const merged = mergeGeometries([leafGeo.clone(), back]);
    back.dispose();
    return merged!;
})();
/** Which end of a branch grows from wood, and the trunk (if any) that carries it. */
type Attachment = { fromA: boolean; trunk?: Trunk };
/** Horizontal distance from a point to a trunk axis. */
const axisDistance = (p: { x: number; z: number }, t: Trunk) => Math.hypot(p.x - t.x, p.z - t.z);
/**
 * A catchable branch: stable knuckle at the base, a gently arched grabbable span with a moss
 * band on top (exactly where hands go), and a younger leafy tip. Visual arc ≤ 0.4·radius. Works
 * for any slope: the parent limb leaves its trunk and arrives tangent to the branch.
 */
function gameplayBranch(batch: Batch, structure: Batch, o: Traversal, attach: Attachment, scatter?: Scatter, tips?: Clearance) {
    const style = branchStyle(o), look = grammar[style];
    const len = v(o.a).distanceTo(v(o.b));
    if (len < .05)
        return;
    const fromA = attach.fromA;
    const at = (s: number) => spanPoint(o, fromA ? s : 1 - s);
    const base = at(0), tip = at(1), dir = tip.clone().sub(base).normalize();
    const baseExt = style === 'strong' ? .6 : .45, tipExt = style === 'flexible' ? 1.15 : style === 'dead' ? .55 : .85;
    const points = [base.clone().addScaledVector(dir, -baseExt).add(new T.Vector3(0, -.2 * Math.sqrt(1 - dir.y * dir.y), 0))];
    const samples = Math.max(4, Math.ceil(len / 1.1));
    for (let i = 0; i <= samples; i++)
        points.push(at(i / samples));
    points.push(tip.clone().addScaledVector(dir, tipExt * .5).add(new T.Vector3(0, .08, 0)), tip.clone().addScaledVector(dir, tipExt).add(new T.Vector3(0, style === 'dead' ? .02 : .3, 0)));
    const curve = new T.CatmullRomCurve3(points, false, 'centripetal');
    const total = baseExt + len + tipExt, u0 = baseExt / total, u1 = (baseExt + len) / total;
    const [kb, kt] = branchTaper(style), rb = o.radius * kb, rt = o.radius * kt;
    const radius = (u: number) => u < u0 ? rb * (1 + .3 * (1 - u / u0) ** 2) : u <= u1 ? T.MathUtils.lerp(rb, rt, (u - u0) / (u1 - u0)) : T.MathUtils.lerp(rt, rt * .25, (u - u1) / (1 - u1));
    const segments = Math.max(12, Math.ceil(total * 2.3)), radial = 8;
    const g = taperedTube(curve, segments, radial, radius);
    const bark = new T.Color(look.bark), moss = new T.Color(look.moss), deco = new T.Color(palette.deco), young = new T.Color(grammar.young), crack = new T.Color(grammar.dead.crack);
    // The branch's parent: a dark, recessive limb back to its trunk (never catchable).
    let parent: T.Vector3[] | null = null, collar = false;
    const t = attach.trunk, start = points[0];
    if (t) {
        const out = new T.Vector3(start.x - t.x, 0, start.z - t.z), d = out.length();
        if (out.lengthSq() < 1e-6) out.set(-dir.x, 0, -dir.z);
        out.normalize();
        if (d <= t.r + .45)
            collar = true;
        else {
            const y = Math.max(t.bottom + 2, Math.min(t.top - .4, start.y - Math.min(2.6, d * .4)));
            const root = new T.Vector3(t.x + out.x * t.r * .55, y, t.z + out.z * t.r * .55);
            const lead = Math.min(1.7, d * .35);
            const c1 = root.clone().addScaledVector(out, d * .38).add(new T.Vector3(0, (start.y - y) * .55 + .25, 0));
            const c2 = start.clone().addScaledVector(dir, -lead);
            parent = [root, c1, c2, start];
        }
    }
    // Where the limb leaves its tree it wears the tree's own bark, warming into the grip colour
    // only along the catchable span, so it reads as growing from that trunk.
    const knuckle = t ? t.bark.clone().lerp(bark, .15) : parent || collar ? deco : bark.clone().multiplyScalar(.82);
    paintTube(g, segments, radial, (u, j, ny, c) => {
        if (u < u0)
            c.copy(knuckle).lerp(bark, smooth(u, u0 * .2, u0));
        else if (u > u1)
            c.copy(bark).lerp(young, smooth(u, u1, u1 + (1 - u1) * .6));
        else {
            c.copy(bark).lerp(moss, smooth(ny, .58, .93) * .72);
            if (style === 'dead' && j % 3 === 1 && Math.sin(u * 41 + j * 2.3) > .15)
                c.copy(crack);
        }
        c.multiplyScalar(.95 + .05 * Math.sin(u * 29 + j * 1.7));
    });
    batch.push('grip', prepare(g, look.bark));
    g.dispose();
    structure.add(t ? 'bark' : 'grip', sphere, start.toArray() as Vec3, [rb * 1.28, rb * 1.28, rb * 1.28], [0, 0, 0], '#' + knuckle.getHexString());
    if (parent) {
        const limb = new T.CatmullRomCurve3(parent, false, 'centripetal'), r0 = Math.min(t!.r * .5, rb * 2.4), r1 = rb * 1.32;
        const lg = taperedTube(limb, Math.max(10, Math.ceil(limb.getLength() * 1.6)), 8, u => u < .1 ? T.MathUtils.lerp(r0 * 1.35, r0, u / .1) : T.MathUtils.lerp(r0, r1, Math.sqrt((u - .1) / .9)));
        lg.computeVertexNormals();
        structure.add('bark', lg, [0, 0, 0], [1, 1, 1], [0, 0, 0], '#' + t!.bark.getHexString());
        lg.dispose();
    }
    else if (collar && t) {
        // The branch grows out of the bark: a thick collar from inside the trunk flares into the
        // limb, and the trunk swells around the joint (no stick poked into a pole).
        const out = new T.Vector3(start.x - t.x, 0, start.z - t.z).normalize(), root = new T.Vector3(t.x + out.x * t.r * .4, start.y - rb * .5, t.z + out.z * t.r * .4);
        const neck = start.clone().addScaledVector(dir, Math.min(.5, len * .15));
        const neckGeo = barkTube([root, start.clone().lerp(root, .35), neck], rb * 2.4, rb, t.bark, t.dark, 8, u => rb * (u < .45 ? T.MathUtils.lerp(2.5, 1.6, u / .45) : T.MathUtils.lerp(1.6, 1.08, (u - .45) / .55)));
        structure.push('bark', prepare(neckGeo, palette.bark));
        neckGeo.dispose();
    }
    else if (collar) {
        const back = start.clone().addScaledVector(dir, -rb * 2.2);
        structure.tube('deco', [back.toArray() as Vec3, start.toArray() as Vec3], rb * 1.9, undefined, .7);
    }
    // Leaf language: strong = a few, standard = a tuft, flexible = leafy tip, dead = one dry leaf.
    const yaw = Math.atan2(dir.x, dir.z), end = points[points.length - 1];
    const tuft = style === 'dead' ? 1 : style === 'strong' ? 2 : style === 'flexible' ? 5 : 3;
    for (let k = 0; k < tuft; k++)
        batch.add('leaf', leafGeo, end.toArray() as Vec3, [.62, .62, .72], [-.35 + k * .08, yaw + (k - (tuft - 1) / 2) * .85, .35], style === 'dead' ? '#b39a5c' : k % 2 ? '#9bb452' : '#6f9e3f');
    if (style === 'flexible')
        for (let k = 0; k < 3; k++) {
            const p = at(.62 + k * .15), side = k % 2 ? 1 : -1;
            batch.add('leaf', leafGeo, [p.x, p.y + .05, p.z], [.42, .42, .5], [-.25, yaw + side * 1.3, side * .3], k % 2 ? '#a7c25a' : '#79a944');
        }
    // Beyond the catchable span the branch forks into leafy branchlets (never inside a flight corridor).
    if (scatter && tips && style !== 'dead') {
        const from = tip.clone().addScaledVector(dir, tipExt * .45), side = new T.Vector3(-dir.z, 0, dir.x).normalize();
        for (const s of o.id % 2 ? [1, -1] : [-1, 1]) {
            const fl = .8 + (o.id * 7 + s * 3) % 5 * .12, end = from.clone().addScaledVector(dir, fl * .75).addScaledVector(side, s * fl * .6).add(new T.Vector3(0, fl * .35, 0));
            if (!tips.free(end.x, end.y, end.z, .9)) continue;
            structure.tube('grip', [from.toArray() as Vec3, from.clone().lerp(end, .5).add(new T.Vector3(0, .08, 0)).toArray() as Vec3, end.toArray() as Vec3], rt * .42, '#' + new T.Color(look.bark).lerp(young, .5).getHexString(), .4);
            const cs = .6 + (o.id % 4) * .07;
            scatter.add(s > 0 ? 'clump' : 'clumpB', { x: end.x, y: end.y + cs * .15, z: end.z }, new T.Vector3(cs, cs * .75, cs), o.id * 1.3 + s, new T.Color(style === 'flexible' ? '#86b94a' : '#6fa843').offsetHSL(0, 0, ((o.id % 5) - 2) * .015), .2);
            break;
        }
    }
}
/** Launch sapling (moving): tapered young rod with a leafy crown that bends with the solver. */
function saplingGeometry(o: Traversal) {
    const a = v(o.a), b = v(o.b), dir = b.clone().sub(a).normalize();
    const curve = new T.CatmullRomCurve3([a, a.clone().lerp(b, .5).add(new T.Vector3(0, .07, 0)), b, b.clone().addScaledVector(dir, .9).add(new T.Vector3(0, .4, 0))]);
    const segments = 24, radial = 8, u1 = a.distanceTo(b) / (a.distanceTo(b) + .95);
    const g = taperedTube(curve, segments, radial, u => u <= u1 ? o.radius * T.MathUtils.lerp(1.05, .62, u / u1) : o.radius * T.MathUtils.lerp(.62, .2, (u - u1) / (1 - u1)));
    const bark = new T.Color(grammar.flexible.bark), moss = new T.Color(grammar.flexible.moss), young = new T.Color(grammar.young);
    paintTube(g, segments, radial, (u, _j, ny, c) => { c.copy(bark).lerp(young, smooth(u, .2, 1)).lerp(moss, u <= u1 ? smooth(ny, .45, .86) * .8 : 0); });
    const parts = [prepare(g, grammar.flexible.bark)];
    g.dispose();
    const end = curve.getPoint(1), yaw = Math.atan2(dir.x, dir.z);
    for (let k = 0; k < 6; k++)
        parts.push(prepare(leafGeo2, k % 2 ? '#a3c254' : '#6fa041', end.toArray() as Vec3, [.8, .8, .9], [-.5 + (k % 3) * .25, yaw + k * 1.05, .3]));
    const merged = mergeGeometries(parts)!;
    parts.forEach(p => p.dispose());
    return merged;
}
/** Vine (moving): a clean, uninterrupted jade liana with a faint twist, leaf collar and tip tuft. */
function vineGeometry(o: Traversal) {
    const a = v(o.a), b = v(o.b), length = a.distanceTo(b);
    const curve = new T.LineCurve3(a, b), segments = Math.max(12, Math.ceil(length * 3)), radial = 7;
    const r = Math.max(o.radius, .07);
    const g = taperedTube(curve, segments, radial, u => r * T.MathUtils.lerp(1.15, .85, u));
    const base = new T.Color(grammar.vine), light = new T.Color(grammar.vineLight);
    paintTube(g, segments, radial, (u, j, _ny, c) => { c.copy(base).lerp(light, Math.sin(u * length * 5 + j * 1.8) > .55 ? .45 : 0); });
    const parts = [prepare(g, grammar.vine)];
    g.dispose();
    for (let k = 0; k < 5; k++)
        parts.push(prepare(leafGeo2, k % 2 ? '#5f9e4b' : '#79b04e', [a.x, a.y - .1, a.z], [.7, .7, .8], [.55, k * 1.26, 0]));
    parts.push(prepare(sphere, grammar.vine, [b.x, b.y, b.z], [r * 1.9, r * 2.4, r * 1.9]));
    for (let k = 0; k < 3; k++)
        parts.push(prepare(leafGeo2, '#86c77c', [b.x, b.y - .05, b.z], [.42, .42, .5], [1.1, k * 2.1, 0]));
    const merged = mergeGeometries(parts)!;
    parts.forEach(p => p.dispose());
    return merged;
}
/** Bounce leaf: an oversized waxy bowl with a pale midrib and raised rim. */
function bowlLeaf(length: number, width: number, depth: number) {
    const positions: number[] = [], colors: number[] = [], indices: number[] = [];
    const U = 14, V = 10, mid = new T.Color('#c8e27d'), body = new T.Color(palette.waxy), edge = new T.Color('#4f8f37'), c = new T.Color();
    for (let i = 0; i <= U; i++) {
        const u = i / U, w = Math.max(.02, Math.sin(Math.PI * u) ** .7) * width / 2;
        for (let j = 0; j <= V; j++) {
            const s = j / V * 2 - 1;
            positions.push(s * w, depth * (s * s * .8 * (w / (width / 2)) + (2 * u - 1) ** 2 * .45), (u - .5) * length);
            c.copy(body).lerp(edge, smooth(Math.abs(s), .55, 1));
            if (Math.abs(s) < .09) c.copy(mid);
            else if (Math.abs(Math.sin((u * 7 + Math.abs(s) * 1.6) * Math.PI)) < .08) c.lerp(mid, .45);
            colors.push(c.r, c.g, c.b);
        }
    }
    for (let i = 0; i < U; i++)
        for (let j = 0; j < V; j++) {
            const a = i * (V + 1) + j;
            indices.push(a, a + V + 1, a + 1, a + 1, a + V + 1, a + V + 2);
        }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(positions, 3));
    g.setAttribute('color', new T.Float32BufferAttribute(colors, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
}
/** Two-wing butterfly; instance Y scale flaps the wings (tips at y=±1 × scale). */
function butterflyGeometry() {
    const p: number[] = [];
    for (const side of [-1, 1]) {
        p.push(0, 0, .12, side * .95, .95, .45, side * .9, .9, -.25);
        p.push(0, 0, .02, side * .7, .65, -.25, side * .45, .5, -.65);
    }
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(p, 3));
    g.computeVertexNormals();
    return g;
}
/** Simple swept chevron bird; instance Y scale beats the wings. */
function birdGeometry() {
    const p: number[] = [];
    for (const side of [-1, 1])
        p.push(0, 0, .35, 0, 0, -.25, side * 1.5, .7, -.45);
    p.push(-.12, 0, .1, .12, 0, .1, 0, 0, .6);
    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(p, 3));
    g.computeVertexNormals();
    return g;
}
/** A group whose geometry swaps by distance (high → low → hidden). */
type LodGroup = { group: T.Group; center: T.Vector3 };
export class Environment {
    root = new T.Group();
    traversal = new T.Group();
    /** Fruit pickups, instanced per kind (two draw calls for any number of fruit). */
    fruit = new Map<string, { mesh: T.InstancedMesh; index: number; base: T.Vector3; tilt: number }>();
    private fruitMeshes: T.InstancedMesh[] = [];
    private fruitDummy = new T.Object3D();
    regions: T.Group[] = [];
    water: T.Mesh;
    waterfalls: T.Mesh[] = [];
    butterflies: T.InstancedMesh;
    birds: T.InstancedMesh;
    sky: Sky;
    /** Shafts of light through the canopy and drifting pollen (two draw calls). */
    light: LightShafts;
    /**
     * Solid decoration the camera must not pass through (synthetic support trunks), plus the map's
     * own objects: pass `solids` to camera occlusion instead of `world.objects`.
     */
    solids: Traversal[] = [];
    /** Dead (breakable) branches, rendered separately so they can crack, fall and regrow. */
    dead = new Map<number, { pivot: T.Group; crack: number; load: number; broken: boolean; fall: number; grow: number; spin: T.Vector3 }>();
    /** Pale bark chips burst from a snapping dead branch. */
    private chips: T.InstancedMesh;
    private chipState: { p: T.Vector3; v: T.Vector3; age: number; spin: number }[] = [];
    private lods: LodGroup[] = [];
    private traversalLods: LodGroup[] = [];
    private clearance: Clearance;
    private wing = new T.Object3D();
    private lifeCenter = new T.Vector3(0, 14, 0);
    private quality = '';
    private elapsed = 0;
    constructor(public scene: T.Scene, public data: WorldData) {
        scene.add(this.root, this.traversal);
        const random = rng(data.seed);
        this.clearance = new Clearance(data);
        this.sky = new Sky(data.seed);
        this.root.add(this.sky.mesh);
        const rippleCanvas = document.createElement('canvas');
        rippleCanvas.width = rippleCanvas.height = 128;
        const rippleContext = rippleCanvas.getContext('2d')!, pixels = rippleContext.createImageData(128, 128);
        for (let y = 0; y < 128; y++)
            for (let x = 0; x < 128; x++) {
                const i = (y * 128 + x) * 4;
                pixels.data[i] = 128 + Math.sin(x * .098 + y * .147) * 22;
                pixels.data[i + 1] = 128 + Math.cos(y * .196 + x * .098) * 17;
                pixels.data[i + 2] = 250;
                pixels.data[i + 3] = 255;
            }
        rippleContext.putImageData(pixels, 0, 0);
        const ripple = new T.CanvasTexture(rippleCanvas);
        ripple.wrapS = ripple.wrapT = T.RepeatWrapping;
        ripple.repeat.set(110, 110);
        // Inviting turquoise (Bible §7), graded with the rest of the world.
        const waterMat = stylize(new T.MeshStandardMaterial({ color: '#2f9e98', normalMap: ripple, normalScale: new T.Vector2(.45, .45), roughness: .26, metalness: .12, transparent: true, opacity: .9 }), { grade: true });
        this.water = new T.Mesh(new T.PlaneGeometry(1200, 1200, 1, 1), waterMat);
        this.water.rotation.x = -Math.PI / 2;
        this.water.position.y = -.1;
        this.water.receiveShadow = true;
        this.root.add(this.water);
        const clear = this.clearance;
        for (const region of data.regions) {
            const batch = new Batch(), plants = new Batch(), scatter = new Scatter(), [x, y, z] = region.center;
            // Broad sandstone islands rise out of the river and support each root system; ferns,
            // bushes and blossoms carpet their tops (understory layer, Bible pillar 3).
            for (let i = 0; i < 6; i++) {
                const a = i * 2.4, r = 4 + random() * 8;
                const cx = x + Math.cos(a) * r, cy = -6 + random() * 3, cz = z + Math.sin(a) * r;
                const sx = 7 + random() * 5, sy = 7 + random() * 7, sz = 7 + random() * 5;
                batch.add('stone', ico, [cx, cy, cz], [sx, sy, sz], [0, random() * 6, 0], region.biome === 'grotto' ? '#8da3a0' : '#b9b493');
                const rr = Math.min(sx, sz);
                for (let k = 0; k < 7; k++) {
                    const fa = random() * Math.PI * 2, fd = rr * (.15 + random() * .6), px = cx + Math.cos(fa) * fd, pz = cz + Math.sin(fa) * fd;
                    const py = cy + sy * .93 * Math.sqrt(Math.max(0, 1 - (fd / rr) ** 2));
                    if (py < .35 || !clear.free(px, py + .8, pz, .9)) continue;
                    if (k % 3 === 2)
                        scatter.add(k % 2 ? 'clump' : 'clumpB', { x: px, y: py + .4, z: pz }, new T.Vector3(1.3 + random(), 1 + random() * .5, 1.3 + random()), random() * 6, CROWNS[Math.floor(random() * CROWNS.length)]);
                    else
                        plants.plant('fern', { x: px, y: py - .1, z: pz }, 1.3 + random() * 1.1, random() * 6, random() < .5 ? '#9ccc5c' : '#7fb54e');
                    if (random() < .35)
                        plants.plant('flower', { x: px + .6, y: py + .25, z: pz }, .28, random() * 6, BLOSSOM[Math.floor(random() * BLOSSOM.length)]);
                }
                // A palm leaning out over the water from some islands (tropical silhouette).
                if (i % 2 === 0 && random() < .7) {
                    const pa = a + (random() - .5), pd = rr * .55, px = cx + Math.cos(pa) * pd, pz = cz + Math.sin(pa) * pd;
                    const py = cy + sy * .93 * Math.sqrt(Math.max(0, 1 - (pd / rr) ** 2)) - .3, h = 7 + random() * 5;
                    const lean = new T.Vector3(Math.cos(pa), 0, Math.sin(pa)).multiplyScalar(h * .32);
                    const top = new T.Vector3(px + lean.x, py + h, pz + lean.z);
                    if (py > .2 && clear.column(px + lean.x * .5, pz + lean.z * .5, py, py + h, .5) && clear.free(top.x, top.y, top.z, 3)) {
                        batch.tube('bark', [[px, py - .5, pz], [px + lean.x * .3, py + h * .5, pz + lean.z * .3], top.toArray() as Vec3], .32, '#9a7650', .55);
                        plants.plant('palm', top, [3.6, 3.2, 3.6], random() * 6, random() < .5 ? '#7cb84a' : '#8cc152');
                    }
                }
            }
            // Jungle trees ring each region, kept off every route and catch volume.
            for (let j = 0; j < 4; j++) {
                const a = j * 2.399 + random() * .3, rad = 22 + random() * 16;
                const tx = x + Math.sin(a) * rad, tz = z + Math.cos(a) * rad, r = .75 + random() * .55;
                const top = Math.max(24, y) + 10 + random() * 11;
                if (!clear.open(tx, tz, 2, top + 4, r + 5)) continue;
                const id = trunkIdentity({ variant: Math.floor(random() * 1e6) }, region.biome, data.seed);
                growTree(batch, scatter, plants, clear, { x: tx, z: tz, r, bottom: -5, top: top - 6, synthetic: true, taken: [], high: -10, ...id }, random, {
                    crownTop: top, size: 2.6 + r * 1.2, hero: false, accents: .25, decor: true,
                });
            }
            // An emergent giant breaks through above the canopy (vertical layering).
            for (let tries = 0; tries < 5; tries++) {
                const a = random() * Math.PI * 2, rad = 26 + random() * 14, tx = x + Math.sin(a) * rad, tz = z + Math.cos(a) * rad;
                const top = Math.max(26, y) + 26 + random() * 10;
                if (!clear.open(tx, tz, 2, top + 4, 6)) continue;
                // Emergents are kapok giants (Cloud Crown / Great Canopy silhouettes), now and then in flower.
                const id = trunkIdentity({ variant: Math.floor(random() * 1e6), species: random() < .3 ? 'flowering' : 'kapok' }, region.biome, data.seed);
                growTree(batch, scatter, plants, clear, { x: tx, z: tz, r: 1.6, bottom: -5, top: top - 8, synthetic: true, taken: [], high: -10, ...id }, random, {
                    crownTop: top, size: 4.4, hero: false, emergent: true, accents: .1, decor: true,
                });
                break;
            }
            if (region.biome === 'village') {
                hut(batch, [x + 1, y + .05, z + 1], 1.1);
                hut(batch, [x - 8, y + 5, z + 2], .7);
                hut(batch, [x + 6, y + 3, z + 6], .75);
            }
            if (region.id === 'home') {
                hut(batch, [x - 5, y + .05, z - 3], .7);
                const sign = this.sign('FIRST RAVINE  →', 'THREE WAYS. YOUR WAY.');
                sign.position.set(x + 2, y + 1.6, z + 3);
                sign.rotation.y = Math.PI;
                this.root.add(sign);
            }
            if (region.biome === 'basin') {
                hut(batch, [x, y, z], 1.15);
                batch.add('roof', sphere, [x + 3, y + 22, z], [4, 5, 4], [0, 0, 0], '#d2a45c');
                for (const dx of [-2, 2])
                    batch.tube('rope', [[x + 3 + dx, y + 18, z], [x + 3 + dx * .3, y + 14, z]], .05);
                batch.add('wood', box, [x + 3, y + 14, z], [2, 1.3, 1.5]);
            }
            if (region.biome === 'temple')
                temple(batch, [x, y - 2, z + 8]);
            if (region.biome === 'cloud') {
                temple(batch, [x, y - 2, z + 6]);
                for (let k = 0; k < 4; k++)
                    batch.add('stone', ico, [x - 8 + k * 5, y - 16, z + 10], [5, 20 + random() * 8, 6]);
            }
            if (region.biome === 'grotto') {
                for (let i = 0; i < 9; i++) {
                    const a = i / 8 * Math.PI;
                    batch.add('stone', ico, [x + Math.cos(a) * 12, y + Math.sin(a) * 13, z + 5], [4, 4, 5], [0, a, .2], '#7c9793');
                }
                for (let k = 0; k < 14; k++)
                    batch.add('stone', new T.ConeGeometry(.6, 3 + random() * 3, 5), [x + (random() - .5) * 16, y, z + 4 + random() * 8], [1, 1, 1], [.3, random() * 6, .3], '#8ebbb5');
            }
            if (region.biome === 'gorge') {
                for (let k = 0; k < 7; k++)
                    batch.add('stone', ico, [x + 8 + k * 3, 8, z + 8], [5, 24 + random() * 5, 6], [0, .2, .1], '#a9b4a1');
                for (let k = 0; k < 3; k++) {
                    const waterfall = new T.Mesh(new T.PlaneGeometry(3.5, 35, 2, 18), this.fallMaterial(k === 1 ? '#f1fbf6' : '#bfeee6'));
                    waterfall.position.set(x + 12 + k * 3, 17, z + 1 - k * .3);
                    waterfall.rotation.y = Math.PI;
                    this.root.add(waterfall);
                    this.waterfalls.push(waterfall);
                }
            }
            if (region.biome === 'mangrove')
                for (let k = 0; k < 8; k++) {
                    const a = k * .78;
                    batch.tube('bark', [[x + Math.sin(a) * 15, -1, z + Math.cos(a) * 15], [x + Math.sin(a) * 8, 7, z + Math.cos(a) * 8], [x, y + 9, z]], .45);
                }
            const group = batch.finish();
            group.add(...plants.finish().children, ...scatter.build().children);
            group.userData.center = v(region.center);
            this.root.add(group);
            this.regions.push(group);
            this.lods.push({ group, center: group.userData.center });
        }
        this.distantScenery(random);
        this.light = new LightShafts(data, rng(data.seed + 4242));
        this.root.add(this.light.group);
        this.rebuildTraversal();
        const bananaGeo = new T.TubeGeometry(new T.CatmullRomCurve3([new T.Vector3(-.27, .14, 0), new T.Vector3(0, -.03, 0), new T.Vector3(.30, .22, 0)]), 10, .10, 7, false);
        const bananaMat = new T.MeshStandardMaterial({ color: '#f9cd50', roughness: .53 }), mangoMat = new T.MeshStandardMaterial({ color: '#f4a64b', roughness: .55 });
        const bananas = data.fruit.filter(f => f.kind === 'banana'), mangoes = data.fruit.filter(f => f.kind !== 'banana');
        const mangoGeo = sphere.clone().scale(.25, .34, .24);
        for (const [list, geo, mat] of [[bananas, bananaGeo, bananaMat], [mangoes, mangoGeo, mangoMat]] as const) {
            if (!list.length) continue;
            const mesh = new T.InstancedMesh(geo, mat, list.length);
            mesh.frustumCulled = false;
            list.forEach((item, index) => this.fruit.set(item.id, { mesh, index, base: v(item.position), tilt: geo === bananaGeo ? .4 : 0 }));
            this.fruitMeshes.push(mesh);
            this.root.add(mesh);
        }
        // Ambient life (Bible pillar 5): instanced butterflies drifting near the player, birds wheeling high.
        this.butterflies = new T.InstancedMesh(butterflyGeometry(), new T.MeshBasicMaterial({ side: T.DoubleSide }), 28);
        const tints = ['#f2a43c', '#e985ad', '#7ec8f0', '#f6d455', '#b39af0', '#ff8f6b'];
        for (let i = 0; i < 28; i++)
            this.butterflies.setColorAt(i, new T.Color(tints[i % tints.length]));
        this.butterflies.frustumCulled = false;
        this.birds = new T.InstancedMesh(birdGeometry(), new T.MeshBasicMaterial({ color: '#3d5157', side: T.DoubleSide, fog: true }), 9);
        this.birds.frustumCulled = false;
        this.chips = new T.InstancedMesh(new T.BoxGeometry(1, .45, 2.2), new T.MeshStandardMaterial({ color: grammar.dead.bark, roughness: 1 }), 48);
        this.chips.count = 0;
        this.chips.visible = false;
        this.chips.frustumCulled = false;
        this.root.add(this.butterflies, this.birds, this.chips);
    }
    private fallMaterial(color: string, far = false) {
        const map = fallTexture.clone();
        map.repeat.set(1, far ? 4 : 2.5);
        map.needsUpdate = true;
        const mat = new T.MeshStandardMaterial({ color, map, emissive: '#7fcfca', emissiveIntensity: .35, transparent: true, opacity: .85, side: T.DoubleSide, roughness: .35, depthWrite: false, fog: !far });
        stylize(mat, far ? { grade: true, haze: .55 } : { grade: true });
        mat.userData.scroll = map;
        return mat;
    }
    /**
     * Layered distance (Bible §3/§8): rings of hazy treeline, mountain massifs, a hero peak and
     * waterfall cliffs, all placed from the map's bounds so any world layout gets a horizon.
     */
    private distantScenery(random: () => number) {
        const regions = this.data.regions;
        const center = new T.Vector3();
        regions.forEach(r => center.add(v(r.center)));
        center.divideScalar(Math.max(1, regions.length)).setY(0);
        let R = 60;
        for (const r of regions) R = Math.max(R, Math.hypot(r.center[0] - center.x, r.center[2] - center.z) + r.radius);
        const spawn = v(this.data.spawn).setY(0), ahead = center.clone().sub(spawn).setY(0);
        if (ahead.lengthSq() < 1) ahead.set(0, 0, 1);
        ahead.normalize();
        const far = new Batch(), trees = new Scatter();
        // Three treeline bands rising away from the play space: hazier and bluer with each band.
        for (let band = 0; band < 3; band++) {
            const radius = R + 32 + band * 42, n = Math.round(Math.PI * 2 * radius / (10 + band * 3));
            for (let i = 0; i < n; i++) {
                const a = (i + random() * .6) / n * Math.PI * 2, d = radius + (random() - .5) * 16;
                const p = new T.Vector3(center.x + Math.cos(a) * d, 0, center.z + Math.sin(a) * d);
                const s = 7 + band * 2.5 + random() * 5;
                p.y = -1 + band * 4.5 + random() * 3.5 + s * .25;
                trees.add('far', p, new T.Vector3(s, s * (.8 + random() * .4), s), random() * 6, CROWNS[Math.floor(random() * CROWNS.length)]);
                if (band === 2 && i % 3 === 0)
                    far.add('far', ico, [p.x, -6, p.z], [s * 2.4, 9 + random() * 6, s * 2.4], [0, random() * 6, 0], '#4f7f55');
            }
        }
        // Mountain massifs in two layers, the nearer ones greener and the outer ones taller.
        for (let i = 0; i < 16; i++) {
            const a = i / 16 * Math.PI * 2 + random() * .2, d = R + 230 + (i % 2) * 90 + random() * 30;
            const h = 70 + random() * 70 + (i % 2) * 45, w = 90 + random() * 70;
            const g = mountainGeometry(i * 13 + 7, 1.25 + random() * .5);
            far.add('far', g, [center.x + Math.cos(a) * d, -4, center.z + Math.sin(a) * d], [w, h, w * (.7 + random() * .4)], [0, random() * 6, 0]);
            g.dispose();
        }
        // The tempting landmark ahead of the spawn: a tall peak flanked by waterfall cliffs.
        const peak = center.clone().addScaledVector(ahead, R + 250);
        const pg = mountainGeometry(991, 1.7);
        far.add('far', pg, [peak.x, -4, peak.z], [120, 230, 110], [0, .4, 0]);
        pg.dispose();
        for (const side of [-1, 1]) {
            const dir = ahead.clone().applyAxisAngle(new T.Vector3(0, 1, 0), side * (.42 + random() * .12));
            const cliff = center.clone().addScaledVector(dir, R + 120 + random() * 30);
            const height = 46 + random() * 18;
            const cg = new T.CylinderGeometry(26, 34, 1, 12, 3);
            far.add('far', cg, [cliff.x, height / 2 - 4, cliff.z], [1, height, 1], [0, random() * 6, 0], '#8a9b84');
            far.add('far', ico, [cliff.x, height - 4, cliff.z], [27, 7, 27], [0, 0, 0], '#4f8a4e');
            cg.dispose();
            // The falls face the play space.
            const toward = center.clone().sub(cliff).setY(0).normalize();
            const fall = new T.Mesh(new T.PlaneGeometry(9, height, 1, 1), this.fallMaterial('#f2fbf8', true));
            fall.position.copy(cliff).addScaledVector(toward, 31.5).setY(height / 2 - 4);
            fall.lookAt(fall.position.clone().add(toward));
            this.root.add(fall);
            this.waterfalls.push(fall);
            far.add('far', sphere, [fall.position.x + toward.x * 3, 0, fall.position.z + toward.z * 3], [16, 5, 16], [0, 0, 0], '#e8f6f4');
        }
        const group = far.finish();
        group.add(...trees.build().children);
        group.traverse(o => { o.castShadow = false; o.receiveShadow = false; });
        this.root.add(group);
    }
    private moving = new Map<number, T.Mesh>();
    rebuildTraversal() {
        this.moving.clear();
        this.dead.clear();
        for (const child of [...this.traversal.children]) {
            this.traversal.remove(child);
            child.traverse(o => {
                if (o instanceof T.Mesh) {
                    if (!o.userData.sharedGeometry) {
                        o.geometry.dispose();
                        if (o.userData.highGeometry && o.userData.highGeometry !== o.geometry)
                            o.userData.highGeometry.dispose();
                        if (o.userData.lowGeometry && o.userData.lowGeometry !== o.geometry)
                            o.userData.lowGeometry.dispose();
                    }
                    if (o instanceof T.InstancedMesh)
                        o.dispose();
                    for (const m of Array.isArray(o.material) ? o.material : [o.material])
                        if (!m.userData.shared)
                            m.dispose();
                }
            });
        }
        this.traversalLods = [];
        // Everything is bucketed by nearest region: per-bucket meshes frustum-cull (main and shadow
        // pass) instead of one world-sized mesh, and each bucket costs one draw per material family.
        const regionsList = this.data.regions;
        const bucketOf = (x: number, z: number) => { let key = 0, best = Infinity; regionsList.forEach((r, k) => { const d = Math.hypot(r.center[0] - x, r.center[2] - z); if (d < best) { best = d; key = k; } }); return key; };
        const buckets = new Map<number, { batch: Batch; plants: Batch; scatter: Scatter; min: T.Vector3; max: T.Vector3 }>();
        const bucket = (x: number, y: number, z: number) => {
            const key = bucketOf(x, z);
            let entry = buckets.get(key);
            if (!entry) buckets.set(key, entry = { batch: new Batch(), plants: new Batch(), scatter: new Scatter(key % 2 ? 'clumpB' : 'clump'), min: new T.Vector3(Infinity, Infinity, Infinity), max: new T.Vector3(-Infinity, -Infinity, -Infinity) });
            entry.min.min(new T.Vector3(x, y, z));
            entry.max.max(new T.Vector3(x, y, z));
            return entry;
        };
        const objects = this.data.objects, clear = new Clearance(this.data);
        // Branch-tip foliage only has to keep out of the flight corridors themselves.
        const tips = new Clearance(this.data, { objects: false, route: 1.1, corridor: () => 1.25 });
        this.clearance = clear;
        const biomeAt = (x: number, z: number) => regionsList[bucketOf(x, z)]?.biome ?? 'gold';
        const trunks: Trunk[] = objects.filter(o => o.type === 5).map(o => ({ id: o.id, x: o.a[0], z: o.a[2], r: o.radius, bottom: Math.min(o.a[1], o.b[1]), top: Math.max(o.a[1], o.b[1]), synthetic: false, taken: [], high: -10, ...trunkIdentity(o as Traversal & { species?: unknown; variant?: unknown }, biomeAt(o.a[0], o.a[2]), this.data.seed) }));
        const branches = objects.filter(o => o.type === 1 || o.type === 7);
        // Where does each branch grow from? Nearest trunk end wins; a branch with no trunk nearby
        // grows from the end farther from its neighbours (branches reach into open air).
        const attachments = new Map<number, Attachment>();
        const orphans: { o: Traversal; base: T.Vector3; out: T.Vector3 }[] = [];
        for (const o of branches) {
            // Authored parent tree (`tree` id) wins; otherwise the nearest trunk within reach.
            const authored = (o as Traversal & { tree?: number }).tree;
            const parentTree = authored == null ? undefined : trunks.find(t => t.id === authored);
            if (parentTree) {
                const da = Math.hypot(o.a[0] - parentTree.x, o.a[2] - parentTree.z), db = Math.hypot(o.b[0] - parentTree.x, o.b[2] - parentTree.z);
                attachments.set(o.id, { fromA: da <= db, trunk: parentTree });
                continue;
            }
            let best: Trunk | undefined, bestD = 16, fromA = true;
            for (const t of trunks) {
                const da = Math.hypot(o.a[0] - t.x, o.a[2] - t.z), db = Math.hypot(o.b[0] - t.x, o.b[2] - t.z);
                if (Math.min(da, db) < bestD) { bestD = Math.min(da, db); best = t; fromA = da <= db; }
            }
            if (best) { attachments.set(o.id, { fromA, trunk: best }); continue; }
            const mid = v(o.a).lerp(v(o.b), .5), near = new T.Vector3();
            let count = 0;
            for (const n of objects)
                if (n !== o && n.type !== 5 && n.type !== 6 && !(n.type === 0 && n.art !== 'bridge' && n.radius > 6)) {
                    const m = v(n.a).lerp(v(n.b), .5);
                    if (m.distanceTo(mid) < 22) { near.add(m); count++; }
                }
            const centroid = count ? near.divideScalar(count) : mid;
            fromA = Math.hypot(o.a[0] - centroid.x, o.a[2] - centroid.z) >= Math.hypot(o.b[0] - centroid.x, o.b[2] - centroid.z);
            attachments.set(o.id, { fromA });
            const base = v(fromA ? o.a : o.b), tip = v(fromA ? o.b : o.a);
            const out = base.clone().sub(tip).setY(0);
            if (out.lengthSq() < 1e-4) out.set(1, 0, 0);
            orphans.push({ o, base, out: out.normalize() });
        }
        // Orphan branches share synthetic support trunks set back behind their bases.
        const supports: { p: T.Vector3; members: typeof orphans }[] = [];
        for (const orphan of orphans) {
            const spot = orphan.base.clone().addScaledVector(orphan.out, 2.8).setY(0);
            let group = supports.find(s => s.p.distanceTo(spot) < 7);
            if (!group) supports.push(group = { p: spot.clone(), members: [] });
            group.members.push(orphan);
            group.p.copy(group.members.reduce((acc, m) => acc.add(m.base.clone().addScaledVector(m.out, 2.8).setY(0)), new T.Vector3()).divideScalar(group.members.length));
        }
        this.solids = objects.slice();
        for (const [i, s] of supports.entries()) {
            const top = Math.max(...s.members.map(m => m.base.y)) + 2.5;
            const t: Trunk = { x: s.p.x, z: s.p.z, r: 1 + Math.min(.4, s.members.length * .05), bottom: -5, top, synthetic: true, taken: [], high: -10, ...trunkIdentity({ id: -1 - i, a: [s.p.x, 0, s.p.z] }, biomeAt(s.p.x, s.p.z), this.data.seed) };
            trunks.push(t);
            for (const m of s.members) attachments.get(m.o.id)!.trunk = t;
            this.solids.push({ id: -1 - i, uuid: `support-${i}`, type: 5, a: [t.x, -3, t.z], b: [t.x, top, t.z], radius: t.r, name: 'Support trunk', tags: ['decor'], region: '' });
        }
        // Record which azimuths gameplay limbs occupy so structural limbs fill the gaps.
        for (const o of branches) {
            const at = attachments.get(o.id)!;
            if (!at.trunk) continue;
            const base = at.fromA ? o.a : o.b;
            at.trunk.taken.push(Math.atan2(base[2] - at.trunk.z, base[0] - at.trunk.x));
            at.trunk.high = Math.max(at.trunk.high, o.a[1], o.b[1]);
        }
        for (const o of objects) {
            if (o.type === 5 || o.type === 6) continue;
            const entry = bucket((o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2, (o.a[2] + o.b[2]) / 2), b = entry.batch, structure = b;
            if (o.type === 0)
                deck(b, o);
            if (o.type === 1)
                gameplayBranch(b, structure, o, attachments.get(o.id)!, entry.scatter, tips);
            if (o.type === 7) {
                const own = new Batch(), at = attachments.get(o.id)!;
                gameplayBranch(own, structure, o, at);
                // Pivot at the attached end: cracks shake about it, regrowth extends from it.
                const pivot = new T.Group(), inner = own.finish(), base = v(at.fromA ? o.a : o.b);
                pivot.position.copy(base);
                inner.position.copy(base).negate();
                pivot.add(inner);
                pivot.userData.object = o;
                const tip = v(at.fromA ? o.b : o.a).sub(base).normalize();
                this.dead.set(o.id, { pivot, crack: 0, load: 0, broken: false, fall: 0, grow: 1, spin: new T.Vector3(-tip.z, 0, tip.x).normalize() });
                this.traversal.add(pivot);
            }
            if (o.type === 2 || o.type === 3) {
                const geo = o.type === 2 ? vineGeometry(o) : saplingGeometry(o);
                const mesh = new T.Mesh(geo, material('grip'));
                mesh.castShadow = true;
                mesh.receiveShadow = true;
                mesh.userData.rest = Float32Array.from(geo.getAttribute('position').array);
                mesh.userData.restNormal = Float32Array.from(geo.getAttribute('normal').array);
                mesh.userData.object = o;
                mesh.userData.bend = 0;
                mesh.userData.direction = v(o.b).sub(v(o.a)).normalize();
                this.moving.set(o.id, mesh);
                this.traversal.add(mesh);
                if (o.type === 3) {
                    // A tall flexible stem with a pronounced curve signals "catapult".
                    const a = v(o.a), d = v(o.b).sub(a).setY(0).normalize();
                    b.tube('deco', [[a.x - d.x * 2.6, -1, a.z - d.z * 2.6], [a.x - d.x * 1.9, a.y * .5, a.z - d.z * 1.9], [a.x - d.x * .45, a.y - .7, a.z - d.z * .45], o.a], o.radius * 1.25, '#8f7a45', .82);
                }
            }
            if (o.type === 4) {
                const bowl = bowlLeaf(o.radius * 2.3, o.radius * 1.65, .6);
                b.push('waxy', prepare(bowl, palette.waxy, [o.a[0], o.a[1] - .16, o.a[2]], [1, 1, 1], [0, (o.id * 1.7) % (Math.PI * 2), 0]));
                bowl.dispose();
                b.tube('deco', [[o.a[0] + .6, -1, o.a[2] - .4], [o.a[0] + .2, o.a[1] * .6, o.a[2] - .1], [o.a[0], o.a[1] - .15, o.a[2]]], .17, '#8f7a45', .7);
            }
        }
        // The river under the swing lines: lily rafts and small mossy islets with giant leaves and
        // ferns, so looking down from a corridor reads as living understory rather than bare water.
        const wet = rng(this.data.seed + 31337);
        for (const route of this.data.routes || []) {
            if (route.purpose !== 'flow') continue;
            for (let i = 1; i < route.points.length; i++) {
                const a = route.points[i - 1], b = route.points[i], len = Math.hypot(b[0] - a[0], b[2] - a[2]);
                for (let d = 0; d < len; d += 6.5) {
                    const t = d / len, x = a[0] + (b[0] - a[0]) * t + (wet() - .5) * 16, z = a[2] + (b[2] - a[2]) * t + (wet() - .5) * 16;
                    if (!clear.free(x, 1.2, z, 1.4)) continue;
                    const plants = bucket(x, 0, z).plants;
                    if (wet() < .3) {
                        const r = 1.2 + wet() * 1.4;
                        bucket(x, 0, z).batch.add('bark', ico, [x, -.75, z], [r * 1.6, 1.1, r * 1.4], [0, wet() * 6, 0], '#8c8a62');
                        plants.plant(wet() < .5 ? 'giant' : 'fern', { x, y: .2, z }, 1.1 + wet() * .8, wet() * 6, wet() < .5 ? '#79b04a' : '#8cc257');
                        plants.plant('fern', { x: x + r * .5, y: .1, z: z - r * .3 }, .9 + wet() * .5, wet() * 6, '#9ccc5c');
                    }
                    else plants.plant('lily', { x, y: .02, z }, 1 + wet() * .9, wet() * 6, wet() < .5 ? '#6fae4c' : '#8cbc58');
                }
            }
        }
        // Hero trees (map trunks) and support trunks join their region's bucket.
        for (const [i, t] of trunks.entries()) {
            const random = rng(this.data.seed + 977 * (i + 1) + Math.round(t.x * 13 + t.z * 7));
            // The crown sits above the highest limb (clumps that would drape into the swing volume rise
            // or drop); structural limbs and the leafy skirt carry foliage down to swing height.
            const crownTop = Math.max(t.top + (t.synthetic ? 3 : 4.5), t.high + 7.5);
            const entry = bucket(t.x, (t.bottom + crownTop) / 2, t.z);
            growTree(entry.batch, entry.scatter, entry.plants, clear, t, random, {
                crownTop, size: t.synthetic ? 3 : 3.1 + t.r * .55, hero: !t.synthetic, crownCount: t.taken.length > 4 ? .8 : 1, accents: t.synthetic ? .15 : .3,
            });
        }
        for (const entry of buckets.values()) {
            const group = entry.batch.finish();
            group.add(...entry.plants.finish().children, ...entry.scatter.build().children);
            group.userData.center = entry.min.clone().add(entry.max).multiplyScalar(.5);
            this.traversal.add(group);
            this.traversalLods.push({ group, center: group.userData.center });
        }
        this.quality = '';
    }
    /**
     * Applies the simulation's shared dead-branch state (`sim.breaks()`), once per frame. A loaded
     * branch creaks, a cracking one shakes harder and sags, a broken one snaps (bark-chip burst),
     * tumbles away and stays hidden until it regrows with a quick grow-in. Returns the ids that
     * snapped this call so the caller can play a crack cue.
     */
    setBreaks(breaks: Map<number, BranchBreak>) {
        const snapped: number[] = [];
        for (const [id, d] of this.dead) {
            const b = breaks.get(id), broken = !!b?.broken;
            if (broken && !d.broken) {
                d.fall = 0;
                snapped.push(id);
                const o = d.pivot.userData.object as Traversal;
                this.burst(v(o.a).lerp(v(o.b), .5));
            }
            if (!broken && d.broken) d.grow = 0;
            d.broken = broken;
            d.crack = b?.crack ?? 0;
            d.load = b?.load ?? 0;
        }
        return snapped;
    }
    private burst(at: T.Vector3) {
        for (let i = 0; i < 18; i++) {
            const a = i * 2.39996, up = 1.5 + (i % 4) * .8;
            this.chipState.push({ p: at.clone().add(new T.Vector3((i % 5 - 2) * .2, 0, (i % 3 - 1) * .2)), v: new T.Vector3(Math.cos(a) * 2.2, up, Math.sin(a) * 2.2), age: 0, spin: i });
        }
        this.chipState = this.chipState.slice(-48);
    }
    sign(title: string, sub: string) { const c = document.createElement('canvas'); c.width = 640; c.height = 192; const ctx = c.getContext('2d')!; ctx.fillStyle = '#bd9053'; ctx.fillRect(0, 0, 640, 192); ctx.strokeStyle = '#75552e'; ctx.lineWidth = 12; ctx.strokeRect(5, 5, 630, 182); ctx.fillStyle = '#283e29'; ctx.font = 'bold 44px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(title, 320, 83); ctx.font = '22px sans-serif'; ctx.fillText(sub, 320, 130); const texture = new T.CanvasTexture(c); texture.colorSpace = T.SRGBColorSpace; return new T.Mesh(new T.PlaneGeometry(3.3, 1), stylize(new T.MeshStandardMaterial({ map: texture, side: T.DoubleSide }), { fade: 'full' })); }
    update(dt: number, position: T.Vector3, collected: Set<string>, quality: string, actors: SimState[] = []) {
        this.elapsed += dt;
        atmosphere.time.value = this.elapsed;
        if (quality !== this.quality) {
            // Foliage is the overdraw hot spot; the light preset keeps its depth path discard-free.
            this.quality = quality;
            setSeeThrough(foliageMat(), quality !== 'low');
            this.light.motes.visible = quality !== 'low';
        }
        for (const [id, mesh] of this.moving) {
            const o = mesh.userData.object as Traversal;
            const actor = actors.find(s => s.grab === id);
            const rest = mesh.userData.rest as Float32Array;
            const pos = mesh.geometry.getAttribute('position');
            const origin = v(o.a), span = v(o.b).sub(origin), lengthSq = span.lengthSq();
            if (lengthSq < .0001)
                continue;
            const point = new T.Vector3(), offset = new T.Vector3();
            if (o.type === 2) {
                // Keep the full vine below the grip, including when the monkey climbs or tucks.
                const target = actor ? v(actor.p).sub(v(actor.anchor)).normalize() : span.clone().normalize();
                const direction = mesh.userData.direction as T.Vector3;
                direction.lerp(target, Math.min(1, dt * (actor ? 40 : 3))).normalize();
                const rotation = new T.Quaternion().setFromUnitVectors(span.clone().normalize(), direction);
                const normals = mesh.geometry.getAttribute('normal'), restNormal = mesh.userData.restNormal as Float32Array;
                for (let i = 0; i < pos.count; i++) {
                    point.fromArray(rest, i * 3).sub(origin).applyQuaternion(rotation).add(origin);
                    pos.setXYZ(i, point.x, point.y, point.z);
                    point.fromArray(restNormal, i * 3).applyQuaternion(rotation);
                    normals.setXYZ(i, point.x, point.y, point.z);
                }
                normals.needsUpdate = true;
            }
            else {
                mesh.userData.bend = actor ? actor.flex : T.MathUtils.lerp(mesh.userData.bend, 0, Math.min(1, dt * 8));
                for (let i = 0; i < pos.count; i++) {
                    point.fromArray(rest, i * 3);
                    const t = Math.max(0, Math.min(1, offset.copy(point).sub(origin).dot(span) / lengthSq));
                    pos.setY(i, point.y + mesh.userData.bend * Math.min(1.3, (t / Math.max(.15, actor?.gripT || .8)) ** 2));
                }
            }
            pos.needsUpdate = true;
            // Rigid vine rotation carries its normals; the sapling's gentle shear keeps rest normals.
            mesh.geometry.computeBoundingSphere();
        }
        for (const d of this.dead.values()) {
            const g = d.pivot, o = g.userData.object as Traversal, base = d.pivot.position;
            g.quaternion.identity();
            if (d.broken) {
                // Snap: the branch drops and tumbles for a moment, then is gone until it regrows.
                d.fall += dt;
                g.visible = d.fall < .9;
                g.children[0].position.copy(base).negate().add(new T.Vector3(0, -4.9 * d.fall * d.fall, 0));
                g.setRotationFromAxisAngle(d.spin, d.fall * 1.6);
                g.scale.setScalar(1);
            }
            else {
                g.visible = true;
                g.children[0].position.copy(base).negate();
                d.grow = Math.min(1, d.grow + dt / .45);
                const grow = 1 - (1 - d.grow) ** 3;
                g.scale.setScalar(.15 + .85 * grow);
                // Creak under load, shake and sag once cracking: a clear "let go soon" warning.
                const tremble = d.load * .006 + d.crack * .03;
                const angle = Math.sin(this.elapsed * 41 + o.id) * tremble + d.crack * .045;
                g.setRotationFromAxisAngle(d.spin, angle);
            }
        }
        if (this.chipState.length || this.chips.count) {
            this.chipState = this.chipState.filter(c => c.age < 1.1);
            this.chipState.forEach((c, i) => {
                c.age += dt; c.v.y -= 9.8 * dt; c.p.addScaledVector(c.v, dt);
                this.wing.position.copy(c.p);
                this.wing.rotation.set(c.spin + c.age * 9, c.spin * 2 + c.age * 7, 0);
                this.wing.scale.setScalar(.09 * (1 - c.age / 1.1) + .02);
                this.wing.updateMatrix();
                this.chips.setMatrixAt(i, this.wing.matrix);
            });
            this.chips.count = this.chipState.length;
            this.chips.visible = this.chips.count > 0;
            this.chips.instanceMatrix.needsUpdate = true;
        }
        const t = this.elapsed;
        const normal = (this.water.material as T.MeshStandardMaterial).normalMap;
        if (normal)
            normal.offset.set(t * .005, t * .003);
        for (const [id, fruit] of this.fruit) {
            this.fruitDummy.position.copy(fruit.base);
            this.fruitDummy.position.y += Math.sin(t * 2 + fruit.base.x) * .09;
            this.fruitDummy.rotation.set(0, t * .8, fruit.tilt, 'YXZ');
            this.fruitDummy.scale.setScalar(collected.has(id) ? 0 : 1);
            this.fruitDummy.updateMatrix();
            fruit.mesh.setMatrixAt(fruit.index, this.fruitDummy.matrix);
        }
        for (const mesh of this.fruitMeshes) mesh.instanceMatrix.needsUpdate = true;
        // Distance LOD, three tiers per region cell: full clumps near, decimated mid, bare clump
        // cores (and no small plants) far; the Light preset starts one tier down and hides sooner.
        const low = quality === 'low', near = low ? 0 : 55, mid = low ? 45 : 130, reach = low ? 140 : 320;
        for (const cell of [...this.lods, ...this.traversalLods]) {
            const distance = cell.center.distanceTo(position);
            cell.group.visible = distance < reach;
            if (!cell.group.visible) continue;
            for (const o of cell.group.children)
                if (o instanceof T.Mesh && o.userData.lowGeometry) {
                    const far = distance > mid;
                    o.visible = !far || !!o.userData.farGeometry;
                    const geometry = far && o.userData.farGeometry ? o.userData.farGeometry : distance > near ? o.userData.lowGeometry : o.userData.highGeometry;
                    if (o.geometry !== geometry) o.geometry = geometry;
                }
        }
        for (const fall of this.waterfalls) {
            const p = fall.geometry.getAttribute('position');
            if (p.count > 4) {
                for (let i = 0; i < p.count; i++)
                    p.setZ(i, Math.sin(p.getY(i) * 1.5 + t * 5 + p.getX(i)) * .13);
                p.needsUpdate = true;
            }
            const scroll = (fall.material as T.MeshStandardMaterial).userData.scroll as T.Texture | undefined;
            if (scroll) scroll.offset.y = t * .9;
        }
        // Life follows the player loosely so motion is always in view, never on the route line itself.
        this.lifeCenter.lerp(position, 1 - Math.exp(-dt * .25));
        const c = this.lifeCenter;
        for (let i = 0; i < 28; i++) {
            const orbit = 5 + (i * 7.3) % 9, speed = .08 + (i % 5) * .025, a = i * 2.39 + t * speed * (i % 2 ? 1 : -1);
            this.wing.position.set(c.x + Math.sin(a) * orbit + Math.sin(t * .9 + i) * .6, c.y - 3 + (i * 3.7) % 9 + Math.sin(t * 1.3 + i * 1.7) * .7, c.z + Math.cos(a * 1.13) * orbit);
            this.wing.rotation.set(0, a + Math.PI / 2 * (i % 2 ? 1 : -1), 0);
            const flap = Math.sin(t * 17 + i * 1.9);
            this.wing.scale.set(.22, .22 * Math.max(.12, Math.abs(flap)) * Math.sign(flap || 1), .22);
            this.wing.updateMatrix();
            this.butterflies.setMatrixAt(i, this.wing.matrix);
        }
        this.butterflies.instanceMatrix.needsUpdate = true;
        for (let i = 0; i < 9; i++) {
            const radius = 26 + i * 4.5, a = i * .7 + t * (.06 + i * .006);
            this.wing.position.set(c.x + Math.sin(a) * radius, c.y + 16 + (i % 3) * 3.5 + Math.sin(t * .4 + i) * 1.5, c.z + Math.cos(a) * radius);
            this.wing.rotation.set(0, a + Math.PI / 2, Math.sin(a) * .15);
            this.wing.scale.set(1, Math.sin(t * (4.5 + i * .3) + i) * .9, 1);
            this.wing.updateMatrix();
            this.birds.setMatrixAt(i, this.wing.matrix);
        }
        this.birds.instanceMatrix.needsUpdate = true;
    }
}
